import {
  memo, useCallback, useDeferredValue, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import type { CSSProperties, DragEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode, Ref, RefObject } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import {
  columnFilteringFeature,
  columnGroupingFeature,
  createFilteredRowModel,
  createGroupedRowModel,
  createSortedRowModel,
  globalFilteringFeature,
  rowExpandingFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import type { ColumnDef, ExpandedState, FilterFn, Row, SortFn, SortingState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import TextInput from '../form/TextInput';
import NumberField from '../form/NumberField';
import SelectField from '../form/SelectField';
import { BLANK_TEXT, ColumnChooserPopover, HeaderFilterPopover, type FilterValue } from './GridPopovers';
import {
  TextCache, cellValue, columnCaption, columnId, headerFilterable, compareValues, computeSummary, groupValueText, highlightText, isTruncated, keyGetter, measureText,
  sortValueOf, summaryText,
} from './model';
import { exportGridToExcel, type ExportNode } from './exportExcel';
import { drawGridPdf } from './exportPdf';
import { adoptDeclared, declaredKeyOf, declaredOf, initialUserState, mergeOrder, moveInOrder, type Declared } from './columnState';
import { fitWidths, widestText, type SizeSpec } from './sizing';
import { GridTooltip, type TipApi } from './GridTooltip';
import type {
  AppGridColumn, AppGridHandle, AppGridProps, GridColumnState, GridKey, GridKeyDownEvent, GridRowDragEvent, GridSortItem, GridSummaryItem,
} from './types';
import './AppGrid.css';

/**
 * AppGrid: the app's data grid, on TanStack Table (row pipeline: search,
 * header filters, grouping, sorting, selection state) and TanStack Virtual
 * (row virtualisation). Everything visual (layout, pinning, resizing,
 * reordering, group / detail / total rows, editing, keyboard) is ours.
 *
 * Props follow DevExtreme DataGrid's names (see types.ts) so a screen moves
 * over by turning its <Column> children into objects.
 *
 * Rendering: the virtualiser lives in GridBody and rows are memoised, so a
 * scroll frame renders only the rows entering the viewport. Any render of
 * AppGrid itself (state or props changed) re-renders the visible rows.
 */

/* ------------------------------------------------------------------ */
/*  TanStack setup                                                     */
/* ------------------------------------------------------------------ */

type R = Record<string, unknown>;

// A stable number per function / array, for telling when a screen passes a new one.
const refIds = new WeakMap<object, number>();
let nextRefId = 1;
function refId(v: unknown): string {
  if (v == null || (typeof v !== 'function' && typeof v !== 'object')) return '';
  let id = refIds.get(v);
  if (id === undefined) {
    id = nextRefId++;
    refIds.set(v, id);
  }
  return String(id);
}

const features = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  columnGroupingFeature,
  rowExpandingFeature,
  rowSelectionFeature,
  filteredRowModel: createFilteredRowModel(),
  groupedRowModel: createGroupedRowModel(),
  sortedRowModel: createSortedRowModel(),
});
type F = typeof features;
type TRow = Row<F, R>;

const INDENT = 24;
const SELECT_W = 44;
/** The select column with selection.showSelectedCount: the box and up to three digits. */
const SELECT_COUNT_W = 64;
/** Touch: how long a finger holds still on a row or header for its menu, and how far it may drift. */
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP = 10;
const DETAIL_W = 32;
const SORT_ICON_W = 22;
const FILTER_ICON_W = 24;
/** The funnel's share of a header past the caption: 4px gap + 20px button + 2px margin. */
const HEADER_FUNNEL_W = 26;
const AUTO_SAMPLE_ROWS = 150;
const AUTO_MAX = 360;
/** A 'fill' column's default minimum, in characters of the cell font. */
const FILL_MIN_CH = 14;
/** Rows rendered off screen to measure a 'content' column drawn by cellRender. */
const MEASURE_ROWS = 12;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Item<T> =
  | { kind: 'group'; id: string; row: TRow; depth: number; leaves: T[]; caption: string; expanded: boolean; colId: string; value: unknown; text: string }
  | { kind: 'data'; id: string; row: TRow; data: T; key: GridKey; depth: number; dataIndex: number; hasPreview?: boolean }
  | { kind: 'detail'; id: string; data: T; key: GridKey }
  /** rowPreview's line under its data row: drawn, never focused or clicked. */
  | { kind: 'preview'; id: string; data: T; key: GridKey; dataIndex: number }
  | { kind: 'groupFooter'; id: string; depth: number; leaves: T[] };

interface LayoutCol {
  id: string;
  kind: 'indent' | 'select' | 'detail' | 'data';
  width: number;
  pin: 'left' | 'right' | null;
  offset: number;
  /** Last left-pinned / first right-pinned: draws the pin edge. */
  edge?: boolean;
}

interface Layout {
  cols: LayoutCol[];
  total: number;
  /** columnHiding: visible columns left out to fit the width (never the user's hidden state). */
  autoHidden: string[];
  /** Every visible data column in display order, the left-out ones included (the export's columns). */
  dataOrder: string[];
  /** Widths of the left-out columns, for the PDF export's proportions. */
  hiddenW: Record<string, number>;
}

const NONE: string[] = [];

interface Metrics {
  rowH: number;
  headerFont: string;
  cellFont: string;
  padX: number;
  /** Header caption letter-spacing, px (canvas does not apply it). */
  headerLs: number;
}

const DEFAULT_METRICS: Metrics = { rowH: 32, headerFont: '600 13px sans-serif', cellFont: '15px sans-serif', padX: 12, headerLs: 0.5 };

/**
 * The draft of a cell editor nobody has typed in yet. The editor then shows
 * the row's value as it is now, and finishing it reports nothing: a cell Tab
 * passes through can never write back a value the screen has since changed
 * (the row it was opened from can be one commit behind).
 */
const UNTOUCHED: unique symbol = Symbol('untouched');

/**
 * The row being dragged (rowDragging), shared by every grid on the page so a row can be
 * dropped on another grid of the same group. HTML5 drag data cannot be read during dragover,
 * so the row travels here rather than in dataTransfer.
 */
let rowDrag: {
  gridId: string;
  group: string | undefined;
  data: unknown;
  item: unknown;
  key: GridKey;
  fromIndex: number;
  /** The source grid's onRemove, read live. */
  onRemove: (e: GridRowDragEvent<unknown>) => void;
} | null = null;

/** Stable callbacks rows call; they read the latest grid state through a ref. */
interface RowActions<T> {
  focus: (it: Item<T>) => void;
  /** `fromCheckbox`: the row's checkbox (starts checkbox selection in 'onClick' mode). */
  select: (rowId: string, mode: 'toggle' | 'range' | 'only', fromCheckbox?: boolean) => void;
  toggleDetail: (rowId: string) => void;
  startEdit: (rowId: string, colId: string) => void;
  /** Close the open editor. `refocus` (Enter, Escape) hands the keyboard back to the grid.
   *  `via` 'enter': the editor closed on Enter (editing.enterKeyDirection may move down). */
  finishEdit: (commit: boolean, refocus?: boolean, via?: 'enter') => void;
  /** Commit, then open the next editable cell. False when there is none. */
  moveEdit: (back: boolean) => boolean;
  /** Enter with editing.enterKeyDirection 'column': commit, then open the same
   *  column on the next row. False when it does not apply or there is none. */
  moveEditDown: () => boolean;
  setDraft: (v: unknown) => void;
  rowClick: (it: Item<T> & { kind: 'data' }, e: ReactMouseEvent) => void;
  rowDblClick: (it: Item<T> & { kind: 'data' }, e: ReactMouseEvent | ReactKeyboardEvent) => void;
  /** The latest press was a finger or pen (not the mouse). */
  touch: () => boolean;
}

/** Everything a row needs. A new object on every AppGrid render; unchanged on scroll-only renders. */
interface RowCtx<T> {
  layout: Layout;
  width: number;
  rowH: number;
  getCol: (id: string) => AppGridColumn<T> | undefined;
  textCache: TextCache<T>;
  search: string;
  searchable: Set<string>;
  /** highlightText: marked, not filtered. */
  hl: string;
  focusId: string | null;
  /** cellNavigation: the focused cell's column id (drawn on the focused row). */
  focusCol: string | null;
  /** grouping.allowCollapsing !== false. */
  groupCollapsible: boolean;
  focusedRowEnabled: boolean;
  rowSelection: Record<string, true>;
  selMode: 'none' | 'single' | 'multiple';
  showChecks: boolean;
  /** selection.canSelect (true when not given). */
  canSelect: (row: T) => boolean;
  editingCell: { rowId: string; colId: string } | null;
  draft: unknown;
  /** The open editor was started by a typed character (editOnKeyPress): caret at the end, no select-all. */
  editSeeded: boolean;
  /** rowDragging: data rows are draggable. */
  draggable: boolean;
  /** rowDragging: the drop place, as an index among the data rows (rows.length = after the last). */
  dropGap: number | null;
  /** Number of data rows on this page (for the drop line after the last one). */
  dataCount: number;
  editing: AppGridProps<T>['editing'];
  masterDetail: AppGridProps<T>['masterDetail'];
  rowPreview: AppGridProps<T>['rowPreview'];
  /** columnHiding.showHiddenInRow: the left-out columns drawn in the line under each row. */
  hiddenLine: string[];
  detailIsOpen: (rowId: string) => boolean;
  rowAlternationEnabled: boolean;
  wordWrap: boolean;
  rowClassName: AppGridProps<T>['rowClassName'];
  cellClassName: AppGridProps<T>['cellClassName'];
  groupItems: GridSummaryItem[];
  footerItems: GridSummaryItem[];
  idPrefix: string;
  rowIndexBase: number;
  actions: RowActions<T>;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function AppGrid<T>(props: AppGridProps<T> & { ref?: Ref<AppGridHandle<T>> }) {
  const {
    dataSource, columns, ariaLabel, height = '100%', className, noDataText = 'Nothing here yet.', noMatchesText = 'No matches.',
    scrolling, paging, sorting: sortingOpt, searchPanel, headerFilter, grouping: groupingOpt, groupPanel, summary,
    columnChooser, export: exportOpt, masterDetail, selection, focusedRowEnabled, editing,
    rowAlternationEnabled = true, showRowLines = true, showColumnLines = false, hoverStateEnabled = true,
    allowColumnResizing, allowColumnReordering, wordWrapEnabled, toolbarItems,
    loading = false, loadingText = 'Loading...', errorText, onRetry,
    highlightText: highlightOpt, cellHintEnabled = true, cellNavigation = false, columnHiding,
  } = props;

  // Function props are read live through a ref, so inline arrows (the norm in
  // screens) don't rebuild the row pipeline on every parent render.
  const live = useRef(props);
  live.current = props;
  const getKey = useCallback((row: T) => keyGetter<T>(live.current.keyExpr)(row), []);

  const autoId = useId().replace(/[^a-zA-Z0-9]/g, '');
  const virtual = scrolling?.mode !== 'standard' && height !== 'auto';
  const sortMode = sortingOpt?.mode ?? 'single';
  const selMode = selection?.mode ?? 'none';
  const checkMode = selection?.showCheckBoxesMode ?? 'always';
  const showChecks = selMode === 'multiple' && checkMode !== 'none';
  const groupCollapsible = groupingOpt?.allowCollapsing !== false;
  // Header filters are on unless a grid turns them off (headerFilter={{ visible: false }}).
  const filterVisible = headerFilter?.visible ?? true;
  const showSortIndexes = sortingOpt?.showSortIndexes !== false;
  const hl = (highlightOpt ?? '').trim();
  // A render-only column (no dataField / calculateCellValue / calculateSortValue) has
  // nothing to sort by, so it is not sortable (as in DevExtreme).
  const canSort = (c: AppGridColumn<T>) => sortMode !== 'none' && c.allowSorting !== false
    && !!(c.dataField || c.calculateCellValue || c.calculateSortValue);

  /* ---------------- columns ---------------- */

  const ids = columns.map(columnId);
  const idKey = ids.join(',');
  // Always the latest column objects (their cellRender closures change with the screen's state).
  const colById = new Map<string, AppGridColumn<T>>();
  columns.forEach((c, i) => colById.set(ids[i], c));
  const colMapRef = useRef(colById);
  colMapRef.current = colById;
  const getCol = useCallback((id: string) => colMapRef.current.get(id), []);

  // Structural signature: what changes the row pipeline or the cell text.
  const sig = columns.map((c, i) => [
    ids[i], c.dataField, c.dataType, typeof c.format === 'function' ? 'fn' : JSON.stringify(c.format ?? ''),
    c.calculateCellValue ? 'C' : '', c.calculateSortValue ? 'S' : '', c.customizeText ? 'T' : '',
    c.lookup ? `L${c.lookup.dataSource.length}` : '', c.allowSearch === false ? 'x' : '', c.sortingMethod ? 'M' : '',
    c.caption ?? '', c.width ?? '', c.minWidth ?? '', c.allowSorting === false ? 's' : '', c.allowHeaderFiltering === false || c.allowFiltering === false ? 'f' : '',
    c.cellValueKey === undefined ? '' : JSON.stringify(c.cellValueKey), c.calculateGroupValue ? 'G' : '', c.groupSortValue ? 'g' : '',
    c.sizing ?? '', c.maxWidth ?? '', c.measureText ? 'm' : '', c.headerCellRender ? 'H' : '', c.hidingPriority ?? '',
  ].join(':')).join('|');

  // What a cell's text comes from, by identity: calculateCellValue, customizeText,
  // a format function, a lookup list. Screens close these over lists that load on
  // their own (Print Schedules' unit names, the facility's meal names), often after
  // the rows. When one changes (or a column's cellValueKey, which is in `sig`), the cached text is checked against the new columns
  // and the cache rebuilt only if it differs, so inline columns (new arrows every
  // render, same text) keep their cache and the row pipeline built on it.
  const textFnKey = columns.map((c) => [
    refId(c.calculateCellValue), refId(c.customizeText), typeof c.format === 'function' ? refId(c.format) : '',
    c.lookup ? refId(c.lookup.dataSource) : '',
  ].join('.')).join('|');
  const textCheck = useRef({ fnKey: textFnKey, rev: 0 });
  const committedText = useRef<TextCache<T> | null>(null);
  if (textCheck.current.fnKey !== textFnKey) {
    const stale = committedText.current?.isStale(dataSource) ?? false;
    textCheck.current = { fnKey: textFnKey, rev: textCheck.current.rev + (stale ? 1 : 0) };
  }
  const textRev = textCheck.current.rev;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const textCache = useMemo(() => new TextCache<T>(getCol), [sig, textRev]);
  useLayoutEffect(() => { committedText.current = textCache; }, [textCache]);

  /* ---------------- state declared by columns ---------------- */

  // Only what changed in the declarations is adopted, for the column it changed on
  // (columnState.ts): hiding a column keeps the user's sort, groups, pins, order, widths.
  const declaredNow = declaredOf(columns, ids);
  const declaredKey = declaredKeyOf(declaredNow);
  const [init] = useState(() => initialUserState(declaredNow, props.defaultColumnState));
  const [grouping, setGrouping] = useState<string[]>(init.grouping);
  const [sorting, setSorting] = useState<SortingState>(init.sorting);
  const [hidden, setHidden] = useState<Record<string, boolean>>(init.hidden);
  const [pinned, setPinned] = useState<Record<string, 'left' | 'right'>>(init.pinned);
  // null: the declared order (the user has not moved a column).
  const [orderState, setOrderState] = useState<string[] | null>(init.order);
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const w: Record<string, number> = {};
    for (const [id, px] of Object.entries(props.defaultColumnState?.widths ?? {})) if (declaredNow.cols[id] && px > 0) w[id] = px;
    return w;
  });
  const [seenDeclared, setSeenDeclared] = useState<{ key: string; d: Declared }>({ key: declaredKey, d: declaredNow });
  if (seenDeclared.key !== declaredKey) {
    // A screen changed what it declares (FinancialTab's groupByDistrict, a column's
    // visible flag, a column added): adopt that change only, as DevExtreme does.
    const next = adoptDeclared(seenDeclared.d, declaredNow, { grouping, sorting, hidden, pinned, order: orderState });
    setSeenDeclared({ key: declaredKey, d: declaredNow });
    if (!sameJson(next.grouping, grouping)) setGrouping(next.grouping);
    if (!sameJson(next.sorting, sorting)) setSorting(next.sorting);
    if (!sameJson(next.hidden, hidden)) setHidden(next.hidden);
    if (!sameJson(next.pinned, pinned)) setPinned(next.pinned);
    if (!sameJson(next.order, orderState)) setOrderState(next.order);
  }
  const order = useMemo(() => orderState ?? (idKey ? idKey.split(',') : []), [orderState, idKey]);
  const idsRef = useRef(ids);
  idsRef.current = ids;

  // Columns whose values are booleans (checkbox columns drawn by a cellRender
  // without dataType 'boolean'): no header filter by default.
  const boolCols = useMemo(() => {
    const out = new Set<string>();
    idsRef.current.forEach((id) => {
      const c = colMapRef.current.get(id);
      if (!c || (!c.dataField && !c.calculateCellValue)) return;
      for (let i = 0; i < dataSource.length && i < 50; i++) {
        const v = cellValue(c, dataSource[i]);
        if (v == null) continue;
        if (typeof v === 'boolean') out.add(id);
        break;
      }
    });
    return out;
  }, [dataSource, idKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const canFilter = (id: string, c: AppGridColumn<T>) => filterVisible && headerFilterable(c, boolCols.has(id));

  const autoExpand = groupingOpt?.autoExpandAll !== false;
  const [expanded, setExpanded] = useState<ExpandedState>(autoExpand ? true : {});
  const [detailOpen, setDetailOpen] = useState<Set<string>>(() => new Set());
  const [searchInput, setSearchInput] = useState(searchPanel?.text ?? '');
  const search = useDeferredValue(searchInput.trim());
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  const [rowSelectionInner, setRowSelectionInner] = useState<Record<string, true>>({});
  const [focusedInner, setFocusedInner] = useState<string | null>(props.defaultFocusedRowKey != null ? String(props.defaultFocusedRowKey) : null);
  const [editingCell, setEditingCell] = useState<{ rowId: string; colId: string } | null>(null);
  const [draft, setDraft] = useState<unknown>(undefined);
  const [editSeeded, setEditSeeded] = useState(false);
  // rowDragging: where a row being dragged over this grid would land (index among data rows).
  const [dropGap, setDropGap] = useState<number | null>(null);
  // cellNavigation: the column of the focused cell (null: the first data column).
  const [focusColState, setFocusCol] = useState<string | null>(null);
  // showCheckBoxesMode 'onClick': checkbox selection has started (DevExtreme's
  // "selection with checkboxes"), so plain clicks toggle rather than replace.
  const [withChecks, setWithChecks] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const anchorRef = useRef<string | null>(null);

  const rowSelection = useMemo<Record<string, true>>(() => {
    if (!props.selectedRowKeys) return rowSelectionInner;
    const o: Record<string, true> = {};
    for (const k of props.selectedRowKeys) o[String(k)] = true;
    return o;
  }, [props.selectedRowKeys, rowSelectionInner]);

  // Controlled when the prop is passed at all, even as undefined (DevExtreme's
  // `focusedRowKey={selected?.key}` with nothing selected means "no focused row").
  const focusControlled = Object.prototype.hasOwnProperty.call(props, 'focusedRowKey');
  const focusedId = focusControlled ? (props.focusedRowKey == null ? null : String(props.focusedRowKey)) : focusedInner;
  // Keyboard position in the header row (index into the layout columns), or null for the body.
  const [headerFocus, setHeaderFocus] = useState<number | null>(null);
  // The keyboard outline shows only after keyboard use (a mouse click keeps the
  // look DevExtreme gives a grid without focusedRowEnabled: nothing).
  const [kbdMode, setKbdMode] = useState(false);

  /* ---------------- searchable columns ---------------- */

  const searchable = useMemo(() => new Set(order.filter((id) => {
    const c = colMapRef.current.get(id);
    return c && !hidden[id] && c.allowSearch !== false && (c.dataField || c.calculateCellValue);
  })), [order, hidden, sig]); // eslint-disable-line react-hooks/exhaustive-deps


  /* ---------------- TanStack columns ---------------- */

  const headerFilterFn = useMemo<FilterFn<F, R>>(() => {
    const fn: FilterFn<F, R> = (row, id, allowed: Set<string>) => {
      const t = textCache.get(row.original as T, id);
      return allowed.has(t === '' ? BLANK_TEXT : t);
    };
    return fn;
  }, [textCache]);

  // Search runs once per row, against one lower-cased string of every
  // searchable column's shown text (cached per row), instead of once per cell.
  // TanStack offers the global filter to one column only (the first searchable).
  const searchKey = [...searchable].join(',');
  const firstSearchable = searchable.values().next().value as string | undefined;
  const firstSearchableRef = useRef(firstSearchable);
  firstSearchableRef.current = firstSearchable;
  const globalFilterFn = useMemo<FilterFn<F, R>>(() => {
    const cols = searchKey ? searchKey.split(',') : [];
    const joined = new WeakMap<object, string>();
    const fn: FilterFn<F, R> = (row, _id, q: string) => {
      const o = row.original as object;
      let t = joined.get(o);
      if (t === undefined) {
        t = cols.map((c) => textCache.get(o as T, c)).join('').toLowerCase();
        joined.set(o, t);
      }
      return t.includes(q);
    };
    return fn;
  }, [textCache, searchKey]);

  const tsColumns = useMemo<ColumnDef<F, R>[]>(() => idKey.split(',').map((id) => {
    const sortFn: SortFn<F, R> = (a, b, cid) => {
      const c = getCol(cid);
      // Group rows of this column: by groupSortValue / calculateGroupValue, read from
      // the group's first row (a group row's `original`).
      const groupKey = c?.groupSortValue ?? c?.calculateGroupValue;
      if (groupKey && a.getIsGrouped() && a.groupingColumnId === cid) {
        return compareValues(groupKey(a.original as T), groupKey(b.original as T));
      }
      const va = a.getValue(cid);
      const vb = b.getValue(cid);
      return c?.sortingMethod ? c.sortingMethod(va, vb) : compareValues(va, vb);
    };
    return {
      id,
      header: id,
      accessorFn: (row: R) => {
        const c = getCol(id);
        return c ? sortValueOf(c, row as T) : undefined;
      },
      sortFn,
      sortUndefined: false,
      enableSorting: true,
      enableMultiSort: true,
      enableGrouping: true,
      enableColumnFilter: true,
      enableGlobalFilter: true,
      filterFn: headerFilterFn,
      getGroupingValue: (row: R) => {
        const c = getCol(id);
        if (c?.calculateGroupValue) return groupValueText(c, c.calculateGroupValue(row as T)) || BLANK_TEXT;
        return textCache.get(row as T, id) || BLANK_TEXT;
      },
    } satisfies ColumnDef<F, R>;
  }), [idKey, getCol, headerFilterFn, textCache]);

  // TanStack caches each row's values (sort keys, group values) for the life of the
  // data array, and does not re-run search when the filter functions change. A copy
  // of the array when the shown text or the searched columns change starts it fresh
  // (a rebuilt text cache, when a lookup list arrives after the rows, is one such change).
  const tableData = useMemo(() => dataSource.slice() as unknown as R[], [dataSource, globalFilterFn, headerFilterFn, textCache]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grouped columns always sort (ascending unless the user flipped them),
  // first, in grouping order: DevExtreme orders groups by value.
  const effectiveSorting = useMemo<SortingState>(() => {
    const byId = new Map(sorting.map((s) => [s.id, s]));
    const head = grouping.map((id) => byId.get(id) ?? { id, desc: false });
    return [...head, ...sorting.filter((s) => !grouping.includes(s.id))];
  }, [sorting, grouping]);

  const columnFilters = useMemo(() => Object.entries(filters).map(([id, value]) => ({ id, value })), [filters]);

  const table = useTable<F, R>({
    features,
    columns: tsColumns,
    data: tableData,
    getRowId: (row) => String(getKey(row as T)),
    state: {
      sorting: effectiveSorting,
      grouping,
      expanded,
      globalFilter: search ? search.toLowerCase() : undefined,
      columnFilters,
      rowSelection,
    },
    onSortingChange: (u) => setSorting((prev) => (typeof u === 'function' ? u(prev) : u)),
    onGroupingChange: (u) => setGrouping((prev) => (typeof u === 'function' ? u(prev) : u)),
    onExpandedChange: (u) => setExpanded((prev) => (typeof u === 'function' ? u(prev) : u)),
    onGlobalFilterChange: () => undefined,
    onColumnFiltersChange: () => undefined,
    onRowSelectionChange: () => undefined,
    globalFilterFn,
    getColumnCanGlobalFilter: (column) => column.id === firstSearchableRef.current,
    enableRowSelection: (row) => !row.getIsGrouped(),
    enableMultiRowSelection: selMode === 'multiple',
    groupedColumnMode: false,
    autoResetExpanded: false,
    manualSorting: !!sortingOpt?.manual,
  });

  const tree = table.getRowModel().rows;

  /* ---------------- flatten to render items ---------------- */

  const groupItemsAll = summary?.groupItems ?? [];
  const footerItems = groupItemsAll.filter((g) => g.showInGroupFooter);
  const detailIsOpen = useCallback(
    (rowId: string) => detailOpen.has(rowId) || (!!masterDetail?.autoExpandAll && !detailOpen.has(`!${rowId}`)),
    [detailOpen, masterDetail?.autoExpandAll],
  );

  const { items: baseItems, leafRows, exportNodes } = useMemo(() => {
    const out: Item<T>[] = [];
    const leaves: T[] = [];
    const nodes: ExportNode<T>[] = [];
    const md = live.current.masterDetail;
    const walk = (rows: TRow[], visible: boolean, sink: ExportNode<T>[]) => {
      for (const r of rows) {
        if (r.getIsGrouped()) {
          const colId = r.groupingColumnId!;
          const c = getCol(colId);
          const groupLeaves = r.getLeafRows().filter((x) => !x.getIsGrouped()).map((x) => x.original as T);
          const text = String(r.groupingValue ?? '');
          const caption = `${c ? columnCaption(c) : colId}: ${text}`;
          const value = c?.calculateGroupValue ? c.calculateGroupValue(r.original as T) : text;
          const isOpen = r.getIsExpanded();
          if (visible) out.push({ kind: 'group', id: r.id, row: r, depth: r.depth, leaves: groupLeaves, caption, expanded: isOpen, colId, value, text });
          const children: ExportNode<T>[] = [];
          sink.push({ kind: 'group', caption, children, leaves: groupLeaves });
          walk(r.subRows, visible && isOpen, children);
          if (visible && isOpen && footerItems.length) out.push({ kind: 'groupFooter', id: `${r.id}#footer`, depth: r.depth, leaves: groupLeaves });
        } else {
          const data = r.original as T;
          leaves.push(data);
          sink.push({ kind: 'data', row: data });
          if (!visible) continue;
          const key = getKey(data);
          const dataIndex = leaves.length - 1;
          const preview = live.current.rowPreview?.({ data, key, rowIndex: dataIndex });
          const hasPreview = preview != null && preview !== '' && preview !== false;
          out.push({ kind: 'data', id: r.id, row: r, data, key, depth: r.depth, dataIndex, hasPreview });
          if (hasPreview) out.push({ kind: 'preview', id: `${r.id}#preview`, data, key, dataIndex });
          if (md?.enabled && detailIsOpen(r.id) && (!md.canExpand || md.canExpand(data))) {
            out.push({ kind: 'detail', id: `${r.id}#detail`, data, key: getKey(data) });
          }
        }
      }
    };
    walk(tree, true, nodes);
    return { items: out, leafRows: leaves, exportNodes: nodes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, expanded, detailIsOpen, masterDetail?.enabled, footerItems.length, !!props.rowPreview]);

  /* ---------------- metrics (theme tokens) ---------------- */

  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const probeHeadRef = useRef<HTMLSpanElement>(null);
  const [metrics, setMetrics] = useState<Metrics>(DEFAULT_METRICS);
  // Bumped when web fonts finish loading: text measured with a fallback face is too narrow.
  const [fontVersion, setFontVersion] = useState(0);
  const [containerW, setContainerW] = useState(0);
  const [headerH, setHeaderH] = useState(0);
  const [footerH, setFooterH] = useState(0);

  useLayoutEffect(() => {
    const read = () => {
      const probe = probeRef.current;
      const head = probeHeadRef.current;
      if (!probe || !head) return;
      const cs = getComputedStyle(probe);
      const hs = getComputedStyle(head);
      // The row height token is the source; the probe (a cell sized by it) is the fallback.
      const token = parseFloat(cs.getPropertyValue('--grid-row-height'));
      const rowH = Math.round(token || probe.getBoundingClientRect().height) || DEFAULT_METRICS.rowH;
      // 'normal' is 0; unreadable (jsdom) keeps the CSS default.
      const ls = hs.letterSpacing === 'normal' ? 0 : parseFloat(hs.letterSpacing);
      const next: Metrics = {
        rowH,
        cellFont: `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`,
        headerFont: `${hs.fontWeight} ${hs.fontSize} ${hs.fontFamily}`,
        padX: parseFloat(cs.paddingLeft) || 12,
        headerLs: Number.isNaN(ls) ? DEFAULT_METRICS.headerLs : ls,
      };
      setMetrics((m) => (m.rowH === next.rowH && m.cellFont === next.cellFont && m.headerFont === next.headerFont && m.padX === next.padX && m.headerLs === next.headerLs ? m : next));
    };
    read();
    // Theme size, dark mode and the font toggle all change attributes / style on <html>.
    const mo = new MutationObserver(read);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-theme-size'] });
    const fontsDone = () => {
      read();
      setFontVersion((v) => v + 1);
    };
    void document.fonts?.ready.then(fontsDone);
    document.fonts?.addEventListener?.('loadingdone', fontsDone);
    return () => {
      mo.disconnect();
      document.fonts?.removeEventListener?.('loadingdone', fontsDone);
    };
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      setContainerW(el.clientWidth);
      setHeaderH(headerRef.current?.offsetHeight ?? 0);
      setFooterH(footerRef.current?.offsetHeight ?? 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (headerRef.current) ro.observe(headerRef.current);
    if (footerRef.current) ro.observe(footerRef.current);
    return () => ro.disconnect();
  }, [summary?.totalItems?.length]);

  /* ---------------- layout: widths, pinning ---------------- */

  const visibleIds = order.filter((id) => colById.has(id) && !hidden[id] && !grouping.includes(id));
  const visibleKey = visibleIds.join(',');

  // Content widths for auto-sized columns (DevExtreme columnAutoWidth), from a sample of rows.
  const contentW = useMemo(() => {
    const out: Record<string, number> = {};
    const sample = dataSource.slice(0, AUTO_SAMPLE_ROWS);
    for (const [id, c] of colMapRef.current) {
      // Custom cells are measured by their text too: close enough for a name link or a diet order.
      if (c.width != null || (!c.dataField && !c.calculateCellValue)) continue;
      let w = 0;
      for (const r of sample) {
        const t = textCache.get(r, id);
        if (t.length * 9 < w) continue; // cheap skip before measuring
        w = Math.max(w, measureText(t, metrics.cellFont));
      }
      out[id] = Math.min(AUTO_MAX, Math.ceil(w + metrics.padX * 2 + 6));
    }
    return out;
  }, [dataSource, textCache, metrics.cellFont, metrics.padX, fontVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // 'content' / 'fill' columns: the widest drawn value of EVERY row (distinct texts
  // measured once), in the cell font; numbers and dates in tabular figures.
  const fullContentW = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [id, c] of colMapRef.current) {
      if (!c.sizing || c.width != null) continue;
      const tabular = c.dataType === 'number' || c.dataType === 'date' || c.dataType === 'datetime';
      let texts: Iterable<string> = [];
      if (c.measureText) texts = dataSource.map((r) => c.measureText!(r));
      else if (c.dataField || c.calculateCellValue) texts = dataSource.map((r) => textCache.get(r, id));
      const w = widestText(texts, metrics.cellFont, tabular);
      out[id] = w > 0 ? Math.ceil(w + metrics.padX * 2 + 6) : 0;
    }
    return out;
  }, [dataSource, textCache, metrics.cellFont, metrics.padX, fontVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // 'content' columns drawn by a cellRender (no measureText): their longest rows are
  // rendered off screen (MeasureLayer) and measured after layout.
  const measureCols = useMemo(() => {
    const out: { id: string; rows: T[] }[] = [];
    for (const [id, c] of colMapRef.current) {
      if (c.sizing !== 'content' || c.width != null || !c.cellRender || c.measureText || hidden[id]) continue;
      const hasText = !!(c.dataField || c.calculateCellValue);
      const rows = hasText
        ? dataSource.map((r, i) => ({ r, n: textCache.get(r, id).length, i })).sort((a, b) => b.n - a.n || a.i - b.i).slice(0, MEASURE_ROWS).map((x) => x.r)
        : dataSource.slice(0, MEASURE_ROWS);
      out.push({ id, rows });
    }
    return out;
  }, [dataSource, textCache, hidden]);
  const [renderW, setRenderW] = useState<Record<string, number>>({});
  const measureRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const next: Record<string, number> = {};
    for (const group of el.querySelectorAll<HTMLElement>('[data-measure]')) {
      let w = 0;
      for (const cell of group.children) w = Math.max(w, cell.getBoundingClientRect().width);
      if (w > 0) next[group.dataset.measure!] = Math.ceil(w + 2);
    }
    setRenderW((prev) => (sameJson(prev, next) ? prev : next));
  }, [measureCols, metrics, fontVersion]);

  /** The header's own width: caption (or 24px for a headerCellRender), padding, sort arrow and funnel room. */
  const headerWidth = (id: string, c: AppGridColumn<T>, reserveSort: boolean) => {
    const caption = columnCaption(c);
    const icons = (reserveSort ? SORT_ICON_W : 0) + (canFilter(id, c) ? FILTER_ICON_W : 0);
    // Canvas does not apply the header's letter-spacing: add it per character.
    const text = c.headerCellRender ? 24 : measureText(caption, metrics.headerFont) + caption.length * metrics.headerLs;
    return Math.ceil(text + metrics.padX * 2 + icons + 6);
  };

  /**
   * The narrowest a header can be without its caption running under the funnel: the
   * caption's longest word (a caption wraps between words), the side padding, and the
   * funnel with its 4px gap, from the CSS geometry (.app-grid-hfilter is 20px wide with a
   * 2px right margin and pulls back into the caption's right padding to 4px from the
   * text), so it holds at every UI size. The sort arrow is not reserved: it shows on one
   * column at a time, and the caption gives way to it (ellipsis, with a tooltip).
   */
  const headerMinWidth = (id: string, c: AppGridColumn<T>) => {
    if (c.headerCellRender) return 0;
    const words = columnCaption(c).split(/\s+/).filter(Boolean);
    const word = words.reduce((w, x) => Math.max(w, measureText(x, metrics.headerFont) + x.length * metrics.headerLs), 0);
    const right = canFilter(id, c) ? HEADER_FUNNEL_W : metrics.padX;
    return Math.ceil(word + metrics.padX + right + 2);
  };

  /**
   * Whether an auto column takes a share of the spare width (no fill column
   * visible). Only long text does: a value that is wider than its header and than
   * FILL_MIN_CH characters. Never a check, number, date, centred or right-aligned
   * column, nor one drawn only by cellRender (nothing measured to go on). Shared
   * out to every column by natural width, the spare made a one-letter flag column
   * (Residents "D") and a short code (Unit "East Low") hundreds of pixels wide.
   */
  const autoGrows = (id: string, c: AppGridColumn<T>, head: number, content: number, ch: number) => {
    if (!c.dataField && !c.calculateCellValue) return false;
    if (boolCols.has(id) || c.dataType === 'boolean' || c.dataType === 'number' || c.dataType === 'date' || c.dataType === 'datetime') return false;
    if (c.alignment === 'center' || c.alignment === 'right') return false;
    return content > head && content > FILL_MIN_CH * ch + metrics.padX * 2 + 6;
  };

  // The expander column, only while some row has details to open: on a list
  // where no row can expand (Residents with no guests) it was an empty strip.
  const detailColumn = !!masterDetail?.enabled
    && (!masterDetail.canExpand || dataSource.some((r) => masterDetail.canExpand!(r)));

  const layout = useMemo<Layout>(() => {
    const sys: LayoutCol[] = [];
    if (grouping.length) sys.push({ id: '__indent', kind: 'indent', width: INDENT * grouping.length, pin: 'left', offset: 0 });
    if (showChecks) sys.push({ id: '__select', kind: 'select', width: selection?.showSelectedCount ? SELECT_COUNT_W : SELECT_W, pin: 'left', offset: 0 });
    if (detailColumn) sys.push({ id: '__detail', kind: 'detail', width: DETAIL_W, pin: 'left', offset: 0 });

    const ch = measureText('0', metrics.cellFont);
    const spec = (id: string): SizeSpec => {
      const c = colMapRef.current.get(id)!;
      if (widths[id] != null) return { kind: 'fixed', natural: widths[id], min: 0 };
      // A set width still never hides the caption under the funnel (a width the user drags is theirs).
      if (c.width != null) return { kind: 'fixed', natural: Math.max(c.width, headerMinWidth(id, c)), min: 0 };
      if (c.sizing === 'content') {
        const w = Math.max(c.minWidth ?? 0, headerWidth(id, c, canSort(c)), fullContentW[id] ?? 0, renderW[id] ?? 0);
        return { kind: 'content', natural: c.maxWidth != null ? Math.max(c.minWidth ?? 0, Math.min(c.maxWidth, w)) : w, min: 0 };
      }
      if (c.sizing === 'fill') {
        const min = Math.max(headerWidth(id, c, canSort(c)), c.minWidth ?? Math.ceil(FILL_MIN_CH * ch + metrics.padX * 2 + 6));
        return { kind: 'fill', natural: Math.max(min, Math.min(c.maxWidth ?? AUTO_MAX, fullContentW[id] ?? 0)), min };
      }
      const reserve = c.allowSorting !== false && sortMode !== 'none';
      const head = headerWidth(id, c, reserve);
      const content = contentW[id] ?? 0;
      return { kind: 'auto', natural: Math.max(c.minWidth ?? 40, head, content), min: 0, grow: autoGrows(id, c, head, content, ch) };
    };

    const vis = visibleKey ? visibleKey.split(',') : [];
    const left = vis.filter((id) => pinned[id] === 'left');
    const right = vis.filter((id) => pinned[id] === 'right');
    const center = vis.filter((id) => !pinned[id]);
    const dataOrder = [...left, ...center, ...right];
    const reserved = sys.reduce((s, c) => s + c.width, 0);
    let dataIds = dataOrder;
    let specs = dataOrder.map(spec);
    let autoHidden = NONE;
    const hiddenW: Record<string, number> = {};
    if (columnHiding?.enabled && containerW > 0) {
      // How narrow a column can get here: a `width` column with a minWidth (not one the
      // user dragged) shrinks to it, a fill column to its minimum; the rest keep their width.
      const floorOf = (id: string, s: SizeSpec) => {
        const c = colMapRef.current.get(id)!;
        if (s.kind === 'fill') return s.min;
        if (s.kind === 'fixed' && widths[id] == null && c.width != null && c.minWidth != null) return Math.min(s.natural, Math.max(c.minWidth, headerMinWidth(id, c)));
        return s.natural;
      };
      let floors = dataOrder.map((id, i) => floorOf(id, specs[i]));
      let total = reserved + floors.reduce((s, w) => s + w, 0);
      // Lowest priority first; equal priorities, the rightmost first.
      const byPriority = dataOrder
        .map((id, i) => ({ id, i, p: colMapRef.current.get(id)!.hidingPriority }))
        .filter((x): x is { id: string; i: number; p: number } => x.p != null)
        .sort((a, b) => a.p - b.p || b.i - a.i);
      const drop = new Set<string>();
      for (const x of byPriority) {
        if (total <= containerW) break;
        drop.add(x.id);
        total -= floors[x.i];
      }
      if (drop.size) {
        autoHidden = dataOrder.filter((id) => drop.has(id));
        dataOrder.forEach((id, i) => { if (drop.has(id)) hiddenW[id] = specs[i].natural; });
        const keep = dataOrder.map((id) => !drop.has(id));
        dataIds = dataOrder.filter((_id, i) => keep[i]);
        specs = specs.filter((_s, i) => keep[i]);
        floors = floors.filter((_w, i) => keep[i]);
      }
      // Still too wide: the shrinkable columns give up width, in proportion to what each can give.
      const over = reserved + specs.reduce((s, sp) => s + (sp.kind === 'fill' ? sp.min : sp.natural), 0) - containerW;
      const give = specs.map((sp, i) => (sp.kind === 'fixed' ? sp.natural - floors[i] : 0));
      const canGive = give.reduce((s, g) => s + g, 0);
      if (over > 0 && canGive > 0) {
        const take = Math.min(over, canGive);
        specs = specs.map((sp, i) => (give[i] > 0 ? { ...sp, natural: sp.natural - Math.min(give[i], Math.ceil((take * give[i]) / canGive)) } : sp));
      }
    }
    const fitted = fitWidths(specs, reserved, containerW);
    const dataCols: LayoutCol[] = dataIds.map((id, i) => ({ id, kind: 'data', width: fitted[i], pin: pinned[id] ?? null, offset: 0 }));

    const all: LayoutCol[] = [...sys, ...dataCols];
    let x = 0;
    for (const c of all) {
      if (c.pin === 'left') {
        c.offset = x;
        x += c.width;
      }
    }
    let y = 0;
    for (let i = all.length - 1; i >= 0; i--) {
      if (all[i].pin === 'right') {
        all[i].offset = y;
        y += all[i].width;
      }
    }
    const lastLeft = all.map((c) => c.pin).lastIndexOf('left');
    const firstRight = all.findIndex((c) => c.pin === 'right');
    if (lastLeft >= 0 && dataCols.some((c) => c.pin === 'left')) all[lastLeft].edge = true;
    if (firstRight >= 0) all[firstRight].edge = true;
    return { cols: all, total: all.reduce((s, c) => s + c.width, 0), autoHidden, dataOrder, hiddenW };
  }, [visibleKey, pinned, widths, containerW, metrics, contentW, fullContentW, renderW, grouping.length, showChecks, selection?.showSelectedCount, detailColumn, sortMode, filterVisible, boolCols, sig, columnHiding?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- render items: hidden-column lines, paging ---------------- */

  // columnHiding.showHiddenInRow: a row whose left-out columns show some text gets the
  // line under it (rowPreview's item, which then draws both).
  const lineKey = columnHiding?.showHiddenInRow ? layout.autoHidden.join(',') : '';
  const items = useMemo(() => {
    if (!lineKey) return baseItems;
    const lineCols = lineKey.split(',').filter((id) => {
      const c = getCol(id);
      return !!c && !!(c.dataField || c.calculateCellValue);
    });
    const out: Item<T>[] = [];
    for (const it of baseItems) {
      if (it.kind !== 'data' || it.hasPreview || !lineCols.some((id) => textCache.get(it.data, id) !== '')) {
        out.push(it);
        continue;
      }
      out.push({ ...it, hasPreview: true }, { kind: 'preview', id: `${it.id}#preview`, data: it.data, key: it.key, dataIndex: it.dataIndex });
    }
    return out;
  }, [baseItems, lineKey, textCache, getCol]);

  const pageSize = paging?.pageSize ?? 100;
  const paged = !virtual && paging?.enabled === true;
  const pageCount = paged ? Math.max(1, Math.ceil(items.length / pageSize)) : 1;
  const viewItems = useMemo(() => (paged ? items.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize) : items), [items, paged, pageIndex, pageSize]);

  const rowWidth = Math.max(layout.total, containerW);

  // cellNavigation: the focused cell's column, kept on a column that is still shown.
  const dataColIds = layout.cols.filter((lc) => lc.kind === 'data').map((lc) => lc.id);
  const focusCol = cellNavigation
    ? (focusColState != null && dataColIds.includes(focusColState) ? focusColState : dataColIds[0] ?? null)
    : null;

  /* ---------------- body api (virtualiser lives in GridBody) ---------------- */

  const bodyApi = useRef<{ scrollToIndex: (i: number) => void }>({ scrollToIndex: () => undefined });

  /* ---------------- focus ---------------- */

  // Touch and pen (tapAgainToOpen, long-press menus): the kind of the latest press in
  // the grid, and the row a finger tapped last while it stays the focused row.
  const pointerRef = useRef('mouse');
  const tapKeyRef = useRef<string | null>(null);
  const isTouch = () => pointerRef.current === 'touch' || pointerRef.current === 'pen';

  const focusIndex = useMemo(() => (focusedId == null ? -1 : viewItems.findIndex((it) => (it.kind === 'data' ? String(it.key) === focusedId : it.kind === 'group' && it.id === focusedId))), [viewItems, focusedId]);

  const setFocus = (it: Item<T> | undefined, scroll = true) => {
    if (!it || it.kind === 'detail' || it.kind === 'preview' || it.kind === 'groupFooter') return;
    const id = it.kind === 'data' ? String(it.key) : it.id;
    if (tapKeyRef.current !== id) tapKeyRef.current = null;
    setHeaderFocus(null);
    if (!focusControlled) setFocusedInner(id);
    if (it.kind === 'data' && id !== focusedId && live.current.focusedRowEnabled) live.current.onFocusedRowChanged?.({ key: it.key, data: it.data, rowIndex: it.dataIndex });
    if (scroll && virtual) {
      const idx = viewItems.indexOf(it);
      if (idx >= 0) bodyApi.current.scrollToIndex(idx);
    }
  };

  /** Bring item `idx` into view: the virtualiser, or the grid's own scroll box. */
  const scrollToItem = (idx: number) => {
    if (virtual) {
      bodyApi.current.scrollToIndex(idx);
      return;
    }
    const box = scrollRef.current;
    const el = document.getElementById(`${autoId}-r-${idx}`);
    if (!box || !el || height === 'auto') return;
    const b = box.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.top < b.top + headerH) box.scrollTop -= b.top + headerH - r.top;
    else if (r.bottom > b.bottom - footerH) box.scrollTop += r.bottom - (b.bottom - footerH);
  };

  // A focused key set from outside is scrolled into view once per key change: at
  // once when its row is there, else when the row appears (the data arrives, its
  // group is expanded, the search that hid it is cleared).
  const lastFocusId = useRef<string | null>(null);
  const pendingScroll = useRef<string | null>(null);
  useEffect(() => {
    if (focusedId !== lastFocusId.current) {
      lastFocusId.current = focusedId;
      pendingScroll.current = focusedId;
    }
    if (pendingScroll.current == null || focusIndex < 0) return;
    pendingScroll.current = null;
    scrollToItem(focusIndex);
  }, [focusedId, focusIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // A removed row hands its place on. When the focused row, or the one selected row of
  // a single-selection grid, leaves dataSource (deleted, or dropped by the screen's own
  // filter), the row now in its place takes over as a user's move would (callbacks fire,
  // it is scrolled to): the next row down, or the row above when it was the last.
  // - Only when the key did not change in the same render: a screen that sets another
  //   key on purpose (clears it, picks a just-saved row) keeps its choice. This effect
  //   runs before the screen's own effects (children first) and before focusRow's pending
  //   row below, so a key the screen sets in an effect of the same commit wins.
  // - Only rows gone from dataSource: a row the grid's own search box or header filter
  //   hides is still there, keeps its focus / selection and is back when the search is
  //   cleared. A full reload that still has the row moves nothing.
  // - Nothing happens while the list is empty (a loading state keeps the last order, so a
  //   delete seen through a reload still finds its neighbour), nor when none of the old
  //   neighbours is in the new list (a different list, e.g. another facility).
  // - Multiple selection is left alone: ticking a row the user did not pick would surprise.
  // - Rows inside a collapsed group are passed by. Opt out with followRemovedRow={false}.
  const removalSnap = useRef<{ order: string[]; focusId: string | null; selId: string | null } | null>(null);
  useEffect(() => {
    const order: string[] = [];
    const byId = new Map<string, Item<T>>();
    for (const it of items) {
      if (it.kind !== 'data') continue;
      order.push(it.id);
      byId.set(it.id, it);
    }
    const selIds = selMode === 'single' ? Object.keys(rowSelection) : [];
    const selId = selIds.length === 1 ? selIds[0] : null;
    const prev = removalSnap.current;
    removalSnap.current = { order: order.length ? order : prev?.order ?? [], focusId: focusedId, selId };
    if (!prev || !order.length || live.current.followRemovedRow === false) return;
    const removed = (id: string | null, was: string | null): id is string => id != null && id === was && !rowById.has(id) && prev.order.includes(id);
    const neighbour = (id: string, ok: (rowId: string) => boolean) => {
      const at = prev.order.indexOf(id);
      for (let i = at + 1; i < prev.order.length; i++) if (byId.has(prev.order[i]) && ok(prev.order[i])) return byId.get(prev.order[i]);
      for (let i = at - 1; i >= 0; i--) if (byId.has(prev.order[i]) && ok(prev.order[i])) return byId.get(prev.order[i]);
      return undefined;
    };
    if (removed(selId, prev.selId)) {
      const next = neighbour(selId, canSelectId);
      if (next) {
        commitSelection({ [next.id]: true });
        anchorRef.current = next.id;
        const idx = viewItems.indexOf(next);
        if (idx >= 0) scrollToItem(idx);
      }
    }
    if (removed(focusedId, prev.focusId)) {
      const next = neighbour(focusedId, () => true);
      if (next) setFocus(next);
    }
  }, [items, focusedId, rowSelection]); // eslint-disable-line react-hooks/exhaustive-deps

  // focusRow(key) for a row that is not there yet: focused when it appears.
  const pendingFocusRow = useRef<string | null>(null);
  // editCell(key, column) for a row that is not there yet: opened when it appears.
  const pendingEdit = useRef<{ key: string; colId: string } | null>(null);
  useEffect(() => {
    const key = pendingFocusRow.current;
    if (key != null) {
      const it = viewItems.find((x) => x.kind === 'data' && String(x.key) === key);
      if (it) {
        pendingFocusRow.current = null;
        setFocus(it);
      }
    }
    const pe = pendingEdit.current;
    if (pe != null) {
      const it = viewItems.find((x) => x.kind === 'data' && String(x.key) === pe.key);
      if (it) {
        pendingEdit.current = null;
        openEditAt(it, pe.colId);
      }
    }
  }, [viewItems]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- selection ---------------- */

  // selection.canSelect, read live. Rows it refuses are passed by everywhere a
  // selection is made (click, Space, Shift range, Select all).
  const canSelectRow = useCallback((row: T) => live.current.selection?.canSelect?.(row) ?? true, []);
  const rowById = useMemo(() => {
    const m = new Map<string, T>();
    for (const r of dataSource) m.set(String(getKey(r)), r);
    return m;
  }, [dataSource, getKey]);
  const canSelectId = (rowId: string) => {
    const r = rowById.get(rowId);
    return r === undefined || canSelectRow(r);
  };

  const commitSelection = (next: Record<string, true>) => {
    if (!live.current.selectedRowKeys) setRowSelectionInner(next);
    if (checkMode === 'onClick') {
      // DevExtreme: more than one row selected starts checkbox selection; an empty selection ends it.
      const n = Object.keys(next).length;
      if (n > 1) setWithChecks(true);
      else if (n === 0) setWithChecks(false);
    }
    const byId = new Map<string, T>();
    for (const r of live.current.dataSource) byId.set(String(getKey(r)), r);
    const data = Object.keys(next).map((k) => byId.get(k)).filter((x): x is T => x !== undefined);
    live.current.onSelectionChanged?.({ selectedRowKeys: data.map((d) => getKey(d)), selectedRowsData: data });
  };

  const dataItemIds = useMemo(() => viewItems.filter((i) => i.kind === 'data').map((i) => i.id), [viewItems]);

  const toggleSelect = (rowId: string, mode: 'toggle' | 'range' | 'only', fromCheckbox = false) => {
    if (selMode === 'none') return;
    if (mode !== 'range' && !canSelectId(rowId)) return;
    if (fromCheckbox && checkMode === 'onClick') setWithChecks(true);
    if (selMode === 'single' || mode === 'only') {
      commitSelection({ [rowId]: true });
      anchorRef.current = rowId;
      return;
    }
    const next = { ...rowSelection };
    if (mode === 'range' && anchorRef.current && dataItemIds.includes(anchorRef.current)) {
      const a = dataItemIds.indexOf(anchorRef.current);
      const b = dataItemIds.indexOf(rowId);
      const [lo, hi] = a < b ? [a, b] : [b, a];
      for (let i = lo; i <= hi; i++) if (canSelectId(dataItemIds[i])) next[dataItemIds[i]] = true;
    } else {
      if (next[rowId]) delete next[rowId];
      else next[rowId] = true;
      anchorRef.current = rowId;
    }
    commitSelection(next);
  };

  // Select all covers the selectable rows only (selection.canSelect).
  const allFilteredIds = useMemo(
    () => table.getFilteredRowModel().rows.filter((r) => canSelectRow(r.original as T)).map((r) => r.id),
    [table, tree, selection?.canSelect], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const selectedCount = allFilteredIds.reduce((n, id) => n + (rowSelection[id] ? 1 : 0), 0);
  // selection.showSelectedCount: every selected key, hidden rows included.
  const totalSelected = Object.keys(rowSelection).length;
  const allSelected = allFilteredIds.length > 0 && selectedCount === allFilteredIds.length;
  const toggleAll = () => {
    if (checkMode === 'onClick') setWithChecks(true);
    const next = { ...rowSelection };
    for (const id of allFilteredIds) {
      if (allSelected) delete next[id];
      else next[id] = true;
    }
    commitSelection(next);
  };

  /* ---------------- sorting / grouping / pinning actions ---------------- */

  // DevExtreme's clicks: plain = sort by this column only (toggling direction),
  // Shift = add / toggle in a multi-sort, Ctrl = remove this column's sort.
  const headerSortClick = (id: string, e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => {
    const c = colById.get(id);
    if (!c || !canSort(c)) return;
    setSorting((prev) => {
      const cur = prev.find((s) => s.id === id);
      if (e.ctrlKey || e.metaKey) return prev.filter((s) => s.id !== id);
      if (e.shiftKey && sortMode === 'multiple') {
        return cur ? prev.map((s) => (s.id === id ? { id, desc: !s.desc } : s)) : [...prev, { id, desc: false }];
      }
      const keepGroups = prev.filter((s) => grouping.includes(s.id) && s.id !== id);
      return [...keepGroups, { id, desc: cur ? !cur.desc : false }];
    });
  };

  const setSort = (id: string, dir: 'asc' | 'desc' | null) => setSorting((prev) => {
    const rest = prev.filter((s) => s.id !== id);
    return dir ? [...rest, { id, desc: dir === 'desc' }] : rest;
  });

  const groupBy = (id: string) => {
    setGrouping((prev) => [...prev.filter((g) => g !== id), id]);
    if (autoExpand) setExpanded(true);
  };
  const ungroup = (id: string) => setGrouping((prev) => prev.filter((g) => g !== id));

  /** Header menu "Move Left / Right": one place among the visible columns of the same pin region. */
  const moveSeq = (id: string) => {
    const pin = layout.cols.find((c) => c.id === id)?.pin ?? null;
    return layout.cols.filter((c) => c.kind === 'data' && c.pin === pin).map((c) => c.id);
  };
  const canMove = (id: string, dir: -1 | 1) => {
    const seq = moveSeq(id);
    const i = seq.indexOf(id);
    return i >= 0 && seq[i + dir] != null;
  };
  const moveColumn = (id: string, dir: -1 | 1) => {
    if (!canMove(id, dir)) return;
    const seq = moveSeq(id);
    setOrderState((prev) => moveInOrder(mergeOrder(prev ?? idsRef.current, idsRef.current), seq, id, dir));
    // The header keyboard position follows the column.
    const hi = layout.cols.findIndex((c) => c.id === id);
    if (headerFocus === hi) setHeaderFocus(hi + dir);
  };


  /* ---------------- detail ---------------- */

  // The open set as last set, so calls in one event (collapse one row, expand another)
  // build on each other, and each change is reported once.
  const detailOpenRef = useRef(detailOpen);
  detailOpenRef.current = detailOpen;
  const setDetail = (rowId: string, open?: boolean) => {
    const prev = detailOpenRef.current;
    const autoAll = !!live.current.masterDetail?.autoExpandAll;
    const isOpen = prev.has(rowId) || (autoAll && !prev.has(`!${rowId}`));
    const want = open ?? !isOpen;
    if (want === isOpen) return;
    const md = live.current.masterDetail;
    const row = live.current.dataSource.find((r) => String(getKey(r)) === rowId);
    // A row without detail (canExpand) does not open, from the keyboard either.
    if (want && row !== undefined && md?.canExpand && !md.canExpand(row)) return;
    const next = new Set(prev);
    next.delete(rowId);
    next.delete(`!${rowId}`);
    if (want) next.add(rowId);
    else if (autoAll) next.add(`!${rowId}`);
    detailOpenRef.current = next;
    setDetailOpen(next);
    if (row === undefined) return;
    if (want) md?.onRowExpanded?.({ key: getKey(row), data: row });
    else md?.onRowCollapsed?.({ key: getKey(row), data: row });
  };

  /* ---------------- header filter ---------------- */

  const [filterFor, setFilterFor] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const filterValues = useMemo<FilterValue[]>(() => {
    if (!filterFor) return [];
    const c = colMapRef.current.get(filterFor.id);
    if (!c) return [];
    const others = Object.entries(filters).filter(([id]) => id !== filterFor.id);
    const buckets = new Map<string, { count: number; sort: unknown }>();
    for (const r of dataSource) {
      let ok = true;
      for (const [id, allowed] of others) {
        const t = textCache.get(r, id);
        if (!allowed.has(t === '' ? BLANK_TEXT : t)) { ok = false; break; }
      }
      if (!ok) continue;
      const t = textCache.get(r, filterFor.id) || BLANK_TEXT;
      const b = buckets.get(t);
      if (b) b.count++;
      else buckets.set(t, { count: 1, sort: t === BLANK_TEXT ? null : sortValueOf(c, r) });
    }
    return [...buckets.entries()]
      .sort((a, b) => (c.sortingMethod ?? compareValues)(a[1].sort, b[1].sort))
      .map(([text, b]) => ({ text, count: b.count }));
  }, [filterFor, filters, dataSource, textCache]);

  /* ---------------- menus, drag, resize ---------------- */

  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [chooserAnchor, setChooserAnchor] = useState<HTMLElement | null>(null);
  const dragId = useRef<string | null>(null);
  const [dropMark, setDropMark] = useState<{ id: string; side: 'before' | 'after' } | null>(null);
  const [panelOver, setPanelOver] = useState(false);
  // The group panel is a pointer drop target only (native listeners); the keyboard
  // groups from the header menu (Shift+F10, "Group by This Column").
  const panelRef = useRef<HTMLDivElement>(null);
  const groupByRef = useRef<(id: string) => void>(() => undefined);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const over = (e: globalThis.DragEvent) => {
      if (!dragId.current) return;
      e.preventDefault();
      setPanelOver(true);
    };
    const leave = () => setPanelOver(false);
    const drop = (e: globalThis.DragEvent) => {
      e.preventDefault();
      setPanelOver(false);
      const id = dragId.current;
      dragId.current = null;
      if (id) groupByRef.current(id);
    };
    el.addEventListener('dragover', over);
    el.addEventListener('dragleave', leave);
    el.addEventListener('drop', drop);
    return () => {
      el.removeEventListener('dragover', over);
      el.removeEventListener('dragleave', leave);
      el.removeEventListener('drop', drop);
    };
  }, [groupPanel?.visible]);
  groupByRef.current = (id: string) => {
    if (colById.get(id)?.allowGrouping !== false) groupBy(id);
  };

  const onHeaderDragStart = (id: string) => (e: DragEvent) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };
  const onHeaderDragOver = (id: string) => (e: DragEvent) => {
    if (!dragId.current || dragId.current === id || !allowColumnReordering) return;
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const side = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
    setDropMark((m) => (m?.id === id && m.side === side ? m : { id, side }));
  };
  const onHeaderDrop = (id: string) => (e: DragEvent) => {
    e.preventDefault();
    const from = dragId.current;
    dragId.current = null;
    setDropMark(null);
    if (!from || from === id) return;
    const side = dropMark?.side ?? 'before';
    setOrderState((prevState) => {
      const prev = prevState ?? idsRef.current;
      const next = prev.filter((x) => x !== from);
      next.splice(next.indexOf(id) + (side === 'after' ? 1 : 0), 0, from);
      return next;
    });
    // Dropping onto a pinned region pins the column there too.
    setPinned((prev) => {
      const next = { ...prev };
      if (prev[id]) next[from] = prev[id];
      else delete next[from];
      return next;
    });
  };

  const startResize = (id: string, startW: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const min = Math.max(30, colById.get(id)?.minWidth ?? 30);
    const x0 = e.clientX;
    const move = (ev: PointerEvent) => setWidths((w) => ({ ...w, [id]: Math.max(min, Math.round(startW + ev.clientX - x0)) }));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  /* ---------------- editing ---------------- */

  /** Whether a cell takes an editor: the column, then (with a row) editing.canEditCell. */
  const canEditCell = (c: AppGridColumn<T> | undefined, row?: T) => !!(editing?.allowUpdating && c && c.dataField && c.allowEditing !== false && !c.calculateCellValue
    && (row === undefined || !editing.canEditCell || editing.canEditCell(row, c)));

  /** editCell: focus the row (and the cell, with cellNavigation), then open the
   *  editor when this row lets that cell edit (editing.canEditCell). */
  const openEditAt = (it: Item<T>, colId: string) => {
    if (it.kind !== 'data') return;
    if (cellNavigation) setFocusCol(colId);
    setFocus(it);
    if (canEditCell(colById.get(colId), it.data)) startEdit(it.id, colId);
  };

  /** Open a cell's editor. `seed`: the character typed to open it (editOnKeyPress). */
  const startEdit = (rowId: string, colId: string, seed?: string) => {
    const c = colById.get(colId);
    if (!c) return;
    setEditingCell({ rowId, colId });
    if (seed === undefined) {
      setDraft(UNTOUCHED);
      setEditSeeded(false);
      return;
    }
    // A number editor takes the typed digit as its number; anything else starts it empty.
    setDraft(c.dataType === 'number' ? (/^\d$/.test(seed) ? Number(seed) : null) : seed);
    setEditSeeded(true);
  };

  /** Data entry (editing.enterKeyDirection 'column'). enterKeyAction 'startEdit' (the default
   *  without editOnKeyPress) opens the editor a row down (moveEditDown); 'moveFocus' (the
   *  default with editOnKeyPress) moves the focused cell down a row (moveFocusDown). */
  const enterDown = editing?.enterKeyDirection === 'column';
  const enterAction = editing?.enterKeyAction ?? (editing?.editOnKeyPress ? 'moveFocus' : 'startEdit');
  const enterMovesFocus = enterDown && enterAction === 'moveFocus';
  const enterMovesEditor = enterDown && enterAction === 'startEdit';
  const moveFocusDown = (rowId: string, colId: string | null) => {
    const rows = viewItems.filter((it) => it.kind === 'data');
    const ri = rows.findIndex((it) => it.id === rowId);
    const target = ri >= 0 ? rows[ri + 1] : undefined;
    if (!target) return;
    if (colId && cellNavigation) setFocusCol(colId);
    setFocus(target);
  };

  const finishEdit = (commit: boolean, refocus = false, via?: 'enter') => {
    const ec = editingCell;
    if (!ec) return;
    setEditingCell(null);
    if (refocus) {
      // Enter / Escape: the keyboard stays on the grid, on the row just edited,
      // rather than dropping to the page when the editor goes.
      setKbdMode(true);
      scrollRef.current?.focus({ preventScroll: true });
    }
    commitDraft(commit, ec);
    // After the commit, so the screen hears of the edit before the focused cell moves.
    if (via === 'enter' && enterMovesFocus) moveFocusDown(ec.rowId, ec.colId);
  };

  const commitDraft = (commit: boolean, ec: { rowId: string; colId: string }) => {
    if (!commit || draft === UNTOUCHED) return;
    const c = colById.get(ec.colId);
    const data = live.current.dataSource.find((r) => String(getKey(r)) === ec.rowId);
    if (!c || !c.dataField || data === undefined) return;
    const old = cellValue(c, data);
    if (old === draft || (old == null && (draft == null || draft === ''))) return;
    const next = { ...(data as object), [c.dataField]: draft } as T;
    live.current.onRowUpdated?.({ key: getKey(data), data: next, oldData: data, dataField: c.dataField, value: draft });
  };

  /**
   * Tab out of a cell editor: commit it and open the next editable cell to
   * the right, wrapping to the next row's first editable cell at the end of
   * one (Shift+Tab mirrors it). Answers false at the very first or last
   * editable cell, where Tab falls through and leaves the grid as usual.
   */
  const moveEdit = (back: boolean): boolean => {
    const ec = editingCell;
    if (!ec) return false;

    const editable = layout.cols.filter((lc) => lc.kind === 'data' && canEditCell(colById.get(lc.id)));
    const rows = viewItems.filter((it): it is Item<T> & { kind: 'data' } => it.kind === 'data');
    let nextCi = editable.findIndex((lc) => lc.id === ec.colId);
    let nextRi = rows.findIndex((it) => it.id === ec.rowId);
    if (nextCi < 0 || nextRi < 0) return false;

    // Step cell by cell, passing cells editing.canEditCell keeps read only on their row.
    for (;;) {
      nextCi += back ? -1 : 1;
      if (nextCi >= editable.length) { nextCi = 0; nextRi += 1; }
      else if (nextCi < 0) { nextCi = editable.length - 1; nextRi -= 1; }
      if (nextRi < 0 || nextRi >= rows.length) return false;
      if (canEditCell(colById.get(editable[nextCi].id), rows[nextRi].data)) break;
    }

    // Both run in one batch, so the editor moves rather than closing first.
    finishEdit(true);
    const target = rows[nextRi];
    startEdit(target.id, editable[nextCi].id);
    // cellNavigation: the focused cell follows the editor.
    if (cellNavigation) setFocusCol(editable[nextCi].id);
    // The focused row (and the grid's active row for screen readers) follows
    // the editor; a virtual list scrolls the row in so its editor can mount.
    setFocus(target);
    return true;
  };

  /** Enter in a data-entry grid (enterKeyDirection 'column', enterKeyAction 'startEdit'):
   *  commit and open the same column on the next row where it is editable. */
  const moveEditDown = (): boolean => {
    const ec = editingCell;
    if (!ec || !enterMovesEditor) return false;
    const c = colById.get(ec.colId);
    const rows = viewItems.filter((it) => it.kind === 'data');
    const ri = rows.findIndex((it) => it.id === ec.rowId);
    if (ri < 0) return false;
    const target = rows.slice(ri + 1).find((it) => canEditCell(c, it.data));
    if (!target) return false;
    finishEdit(true);
    startEdit(target.id, ec.colId);
    if (cellNavigation) setFocusCol(ec.colId);
    setFocus(target);
    return true;
  };

  /* ---------------- stable row actions ---------------- */

  const latest = useRef<RowActions<T>>(null as unknown as RowActions<T>);
  latest.current = {
    focus: (it) => setFocus(it, false),
    select: toggleSelect,
    toggleDetail: (rowId) => setDetail(rowId),
    startEdit,
    finishEdit,
    moveEdit,
    moveEditDown,
    setDraft,
    rowClick: (it, e) => {
      setFocus(it, false);
      if (selMode === 'single') toggleSelect(it.id, 'only');
      else if (selMode === 'multiple') {
        if (e.shiftKey) toggleSelect(it.id, 'range');
        else if (e.ctrlKey || e.metaKey || !showChecks || (checkMode === 'onClick' && withChecks)) toggleSelect(it.id, 'toggle');
        else if (checkMode === 'onClick') toggleSelect(it.id, 'only');
      }
      live.current.onRowClick?.({ data: it.data, key: it.key, rowIndex: it.dataIndex, isSelected: !!rowSelection[it.id], isFocused: true, event: e });
      if (!(e.target as HTMLElement).closest('input,button,a,select,textarea,[role="combobox"],.app-grid-editor,[data-edit],.is-editing')) scrollRef.current?.focus({ preventScroll: true });
    },
    rowDblClick: (it, e) => live.current.onRowDblClick?.({ data: it.data, key: it.key, rowIndex: it.dataIndex, isSelected: !!rowSelection[it.id], isFocused: true, event: e }),
    touch: isTouch,
  };

  /** onRowContextMenu for a data row; the row becomes the focused row. */
  const rowContextMenu = (it: Item<T> & { kind: 'data' }, e: ReactMouseEvent | ReactKeyboardEvent, source: 'mouse' | 'keyboard' | 'touch', x: number, y: number) => {
    setFocus(it, source === 'keyboard');
    live.current.onRowContextMenu?.({ data: it.data, key: it.key, rowIndex: it.dataIndex, isSelected: !!rowSelection[it.id], isFocused: true, event: e, x, y, source });
  };
  // Shift+F10 / the menu key fire a native contextmenu on the grid too (browser-dependent): skip that one.
  const kbdMenuAt = useRef(0);
  const actions = useMemo<RowActions<T>>(() => ({
    focus: (it) => latest.current.focus(it),
    select: (id, m, c) => latest.current.select(id, m, c),
    toggleDetail: (id) => latest.current.toggleDetail(id),
    startEdit: (r, c) => latest.current.startEdit(r, c),
    finishEdit: (c, f, v) => latest.current.finishEdit(c, f, v),
    moveEdit: (b) => latest.current.moveEdit(b),
    moveEditDown: () => latest.current.moveEditDown(),
    setDraft: (v) => latest.current.setDraft(v),
    rowClick: (it, e) => latest.current.rowClick(it, e),
    rowDblClick: (it, e) => latest.current.rowDblClick(it, e),
    touch: () => latest.current.touch(),
  }), []);

  /* ---------------- keyboard ---------------- */

  /** The grid is ONE tab stop. Keys move an active position (aria-activedescendant)
   *  through the header row and the rows; nothing inside the grid is in the tab order. */
  const onGridKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return; // editors and detail content handle their own keys
    if (live.current.onKeyDown) {
      // The screen sees the key first (DevExtreme onKeyDown); preventDefault = handled.
      const cur = headerFocus == null && focusIndex >= 0 ? viewItems[focusIndex] : undefined;
      const ev: GridKeyDownEvent<T> = { event: e };
      if (cur?.kind === 'data') {
        ev.data = cur.data;
        ev.key = cur.key;
        ev.rowIndex = cur.dataIndex;
        if (focusCol) {
          ev.columnId = focusCol;
          ev.column = colById.get(focusCol);
        }
      }
      live.current.onKeyDown(ev);
      if (e.defaultPrevented) return;
    }
    if (headerFocus != null) {
      onHeaderKeyDown(e);
      return;
    }
    const focusable: number[] = [];
    viewItems.forEach((it, i) => { if (it.kind === 'data' || it.kind === 'group') focusable.push(i); });
    const cur = focusIndex >= 0 ? focusable.indexOf(focusIndex) : -1;
    // Up from the first row (or any arrow with no rows) goes to the header row.
    if ((e.key === 'ArrowUp' && cur <= 0) || (!focusable.length && (e.key === 'ArrowUp' || e.key === 'ArrowDown'))) {
      e.preventDefault();
      setHeaderFocus(firstHeaderCol());
      return;
    }
    if (!focusable.length) return;
    const pageRows = Math.max(1, Math.floor(((scrollRef.current?.clientHeight || 400) - headerH - footerH) / metrics.rowH) - 1);
    const go = (pos: number) => {
      e.preventDefault();
      setFocus(viewItems[focusable[Math.max(0, Math.min(focusable.length - 1, pos))]]);
    };
    const it = focusIndex >= 0 ? viewItems[focusIndex] : undefined;
    // Data entry: typing on the focused editable cell opens its editor with that character
    // (editOnKeyPress); F2 opens it as it is.
    if (editing?.editOnKeyPress && cellNavigation && it?.kind === 'data' && focusCol && canEditCell(colById.get(focusCol), it.data)) {
      const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !(e.key === ' ' && selMode !== 'none');
      if (printable || e.key === 'F2') {
        e.preventDefault();
        startEdit(it.id, focusCol, printable ? e.key : undefined);
        return;
      }
    }
    switch (e.key) {
      case 'ArrowDown': return go(cur < 0 ? 0 : cur + 1);
      case 'ArrowUp': return go(cur < 0 ? 0 : cur - 1);
      case 'PageDown': return go(cur < 0 ? 0 : cur + pageRows);
      case 'PageUp': return go(cur < 0 ? 0 : cur - pageRows);
      case 'Home': return go(0);
      case 'End': return go(focusable.length - 1);
      case 'ArrowRight':
      case 'ArrowLeft': {
        if (!it) return;
        const open = e.key === 'ArrowRight';
        if (cellNavigation && it.kind === 'data') {
          // Cell focus: step across the data columns, stopping at either end.
          e.preventDefault();
          const ci = focusCol ? dataColIds.indexOf(focusCol) : -1;
          const next = dataColIds[Math.max(0, Math.min(dataColIds.length - 1, ci + (open ? 1 : -1)))];
          if (next) setFocusCol(next);
          return;
        }
        if (it.kind === 'group') { e.preventDefault(); if (groupCollapsible) it.row.toggleExpanded(open); }
        else if (it.kind === 'data' && masterDetail?.enabled) { e.preventDefault(); setDetail(it.id, open); }
        return;
      }
      case 'Enter': {
        if (!it) return;
        e.preventDefault();
        if (it.kind === 'group') { if (groupCollapsible) it.row.toggleExpanded(); }
        else if (it.kind === 'data' && enterMovesFocus) moveFocusDown(it.id, focusCol);
        else if (it.kind === 'data') {
          const first = layout.cols.find((lc) => lc.kind === 'data' && canEditCell(colById.get(lc.id), it.data));
          if (first) startEdit(it.id, first.id);
          else latest.current.rowDblClick(it, e);
        }
        return;
      }
      case 'F10':
      case 'ContextMenu': {
        if ((e.key === 'F10' && !e.shiftKey) || it?.kind !== 'data' || !live.current.onRowContextMenu) return;
        e.preventDefault();
        kbdMenuAt.current = Date.now();
        // Anchor under the row's first data cell.
        const rowEl = document.getElementById(`${autoId}-r-${focusIndex}`);
        const r = (rowEl?.querySelector('[data-col]') ?? rowEl)?.getBoundingClientRect();
        rowContextMenu(it, e, 'keyboard', r ? r.left + 8 : 0, r ? r.bottom : 0);
        return;
      }
      case ' ': {
        if (it?.kind === 'data' && selMode !== 'none') {
          e.preventDefault();
          // 'onClick' checkboxes: Space picks the row alone until checkbox selection has started.
          const alone = checkMode === 'onClick' && !withChecks && !e.ctrlKey && !e.metaKey;
          toggleSelect(it.id, e.shiftKey ? 'range' : alone ? 'only' : 'toggle');
        }
        return;
      }
      case 'a':
        if ((e.ctrlKey || e.metaKey) && selMode === 'multiple') { e.preventDefault(); toggleAll(); }
        return;
      default:
    }
  };

  const firstHeaderCol = () => {
    const i = layout.cols.findIndex((c) => c.kind === 'data' || c.kind === 'select');
    return i < 0 ? 0 : i;
  };

  const onHeaderKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const cols = layout.cols;
    const reachable = cols.map((c, i) => (c.kind === 'data' || c.kind === 'select' ? i : -1)).filter((i) => i >= 0);
    const at = Math.max(0, reachable.indexOf(headerFocus!));
    const lc = cols[headerFocus!];
    const c = lc && lc.kind === 'data' ? colById.get(lc.id) : undefined;
    const move = (pos: number) => {
      e.preventDefault();
      setHeaderFocus(reachable[Math.max(0, Math.min(reachable.length - 1, pos))] ?? null);
    };
    const cellEl = () => document.getElementById(`${autoId}-h-${headerFocus}`);
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        if ((e.ctrlKey || e.metaKey) && c && allowColumnResizing && c.allowResizing !== false) {
          // Ctrl+Left / Right resizes the column, 10px a step.
          e.preventDefault();
          setWidths((w) => ({ ...w, [lc.id]: Math.max(c.minWidth ?? 30, lc.width + dir * 10) }));
          return;
        }
        return move(at + dir);
      }
      case 'Home': return move(0);
      case 'End': return move(reachable.length - 1);
      case 'ArrowDown': {
        e.preventDefault();
        if (e.altKey && c && canFilter(lc.id, c)) {
          const btn = cellEl()?.querySelector<HTMLElement>('.app-grid-hfilter');
          if (btn) setFilterFor({ id: lc.id, anchor: btn });
          return;
        }
        setHeaderFocus(null);
        const first = viewItems.find((it) => it.kind === 'data' || it.kind === 'group');
        if (focusIndex < 0 && first) setFocus(first);
        return;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (lc?.kind === 'select') toggleAll();
        else if (c) headerSortClick(lc.id, e);
        return;
      }
      case 'F10':
      case 'ContextMenu': {
        // ContextMenu or Shift+F10 opens the column menu.
        if (!c || (e.key === 'F10' && !e.shiftKey)) return;
        e.preventDefault();
        const r = cellEl()?.getBoundingClientRect();
        if (r) setMenu({ id: lc.id, x: r.left, y: r.bottom });
        return;
      }
      case 'Escape':
        setHeaderFocus(null);
        return;
      default:
    }
  };

  /** Focus back on the grid after a popup closes, so the single tab stop keeps the keyboard. */
  const refocusGrid = () => requestAnimationFrame(() => scrollRef.current?.focus({ preventScroll: true }));

  /** Clicks inside the body, handled once on the grid: the row and cell come from
   *  data-index / data-col. Nested grids (in detail rows) handle their own. */
  const itemFromEvent = (e: ReactMouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    if (t.closest('.app-grid-scroll') !== e.currentTarget) return null;
    const rowEl = t.closest<HTMLElement>('.app-grid-body [role="row"][data-index]');
    if (!rowEl) return null;
    const idx = Number(rowEl.dataset.index);
    let it: Item<T> | undefined = viewItems[idx];
    // Touch: the line under a row (rowPreview, columnHiding's left-out columns) is part
    // of its row, so a finger can tap or long-press it. The mouse has the row itself.
    if (it?.kind === 'preview' && isTouch()) {
      const rowId = it.id.slice(0, -'#preview'.length);
      const prev = viewItems[idx - 1];
      it = prev?.kind === 'data' && prev.id === rowId ? prev : items.find((x) => x.kind === 'data' && x.id === rowId);
    }
    const cellEl = t.closest<HTMLElement>('[data-col]');
    return it ? { it, cellEl } : null;
  };

  /** A double-click, or a finger's tap again: a double-click editor, else onRowDblClick. */
  const openRow = (it: Item<T> & { kind: 'data' }, cellEl: HTMLElement | null, e: ReactMouseEvent) => {
    if (cellEl?.dataset.edit === 'dbl') {
      startEdit(it.id, cellEl.dataset.col!);
      return;
    }
    latest.current.rowDblClick(it, e);
  };

  const onGridClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const hit = itemFromEvent(e);
    if (!hit) return;
    const { it, cellEl } = hit;
    if (it.kind === 'group') {
      setFocus(it, false);
      if (groupCollapsible) it.row.toggleExpanded();
      scrollRef.current?.focus({ preventScroll: true });
      return;
    }
    if (it.kind !== 'data') return;
    const colId = cellEl?.dataset.col;
    // tapAgainToOpen: a finger's tap on the row it tapped last opens it, after the click's
    // own work, as a double-click follows its clicks. Not a tap that opens an editor or
    // lands on a control in the cell (a link, a button): those do their own thing.
    const key = String(it.key);
    const again = isTouch() && live.current.tapAgainToOpen !== false && tapKeyRef.current === key
      && cellEl?.dataset.edit !== 'click' && !(e.target as HTMLElement).closest('input,button,a,select,textarea,[role="combobox"],.app-grid-editor,.is-editing');
    if (colId && cellNavigation) setFocusCol(colId);
    if (colId && cellEl?.dataset.edit === 'click') startEdit(it.id, colId);
    const col = colId ? colById.get(colId) : undefined;
    if (col && colId) {
      live.current.onCellClick?.({
        data: it.data, key: it.key, rowIndex: it.dataIndex, isSelected: !!rowSelection[it.id], isFocused: true,
        column: col, columnId: colId, event: e,
      });
    }
    latest.current.rowClick(it, e);
    tapKeyRef.current = isTouch() ? key : null;
    if (again) openRow(it, cellEl, e);
  };

  const onGridDoubleClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    // A finger's double tap: its second tap already opened the row (tapAgainToOpen).
    if (isTouch() && live.current.tapAgainToOpen !== false) return;
    const hit = itemFromEvent(e);
    if (!hit || hit.it.kind !== 'data') return;
    openRow(hit.it, hit.cellEl, e);
  };

  /* ---------------- touch: long-press for the row and header menus ---------------- */

  // A finger held still on a row (onRowContextMenu) or a column header (the column
  // menu) opens its menu: iOS fires no contextmenu, and Android's comes from the same
  // press. Moving, scrolling or lifting first cancels it.
  const press = useRef<{ x: number; y: number; timer: number; fire: () => void } | null>(null);
  // When a long-press opened a menu: the contextmenu and the click that follow belong to it.
  const pressedAt = useRef(0);
  const swallowClick = useRef(false);
  const cancelPress = () => {
    if (press.current) window.clearTimeout(press.current.timer);
    press.current = null;
  };
  const firePress = () => {
    const p = press.current;
    cancelPress();
    if (!p) return;
    pressedAt.current = Date.now();
    swallowClick.current = true;
    p.fire();
  };
  useEffect(() => () => { if (press.current) window.clearTimeout(press.current.timer); }, []);

  const onGridPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointerRef.current = e.pointerType || 'mouse';
    cancelPress();
    swallowClick.current = false;
    if (!isTouch() || !e.isPrimary) return;
    const t = e.target as HTMLElement;
    if (t.closest('.app-grid-scroll') !== e.currentTarget || t.closest('.app-grid-editor, .app-grid-resizer, input, textarea, select')) return;
    const x = e.clientX;
    const y = e.clientY;
    let fire: (() => void) | null = null;
    const hcell = t.closest<HTMLElement>('.app-grid-head [role="columnheader"]');
    if (hcell) {
      const lc = layout.cols[Number(hcell.getAttribute('aria-colindex')) - 1];
      if (lc?.kind === 'data') fire = () => setMenu({ id: lc.id, x, y });
    } else if (live.current.onRowContextMenu) {
      const hit = itemFromEvent(e);
      if (hit?.it.kind === 'data') {
        const it = hit.it;
        fire = () => rowContextMenu(it, e, 'touch', x, y);
      }
    }
    if (fire) press.current = { x, y, fire, timer: window.setTimeout(firePress, LONG_PRESS_MS) };
  };
  const onGridPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const p = press.current;
    if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP) cancelPress();
  };
  /** A contextmenu during or just after a long-press (Android's own): the press's menu, once. */
  const pressContextMenu = (e: ReactMouseEvent) => {
    if (press.current) firePress();
    if (Date.now() - pressedAt.current > 1000) return false;
    e.preventDefault();
    return true;
  };

  /** Right-click on a row (onRowContextMenu). The header has its own column menu; editors keep the browser's. */
  const onGridContextMenu = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (pressContextMenu(e) || !live.current.onRowContextMenu) return;
    const t = e.target as HTMLElement;
    if (t.closest('.app-grid-scroll') !== e.currentTarget || t.closest('.app-grid-head, .app-grid-editor, input, textarea, select')) return;
    if (t === e.currentTarget) {
      // From the keyboard (Shift+F10 / menu key on the grid itself): handled on keydown.
      e.preventDefault();
      if (Date.now() - kbdMenuAt.current < 1000) return;
      const it = focusIndex >= 0 ? viewItems[focusIndex] : undefined;
      if (it?.kind !== 'data') return;
      const r = document.getElementById(`${autoId}-r-${focusIndex}`)?.querySelector('[data-col]')?.getBoundingClientRect();
      rowContextMenu(it, e, 'keyboard', r ? r.left + 8 : 0, r ? r.bottom : 0);
      return;
    }
    const hit = itemFromEvent(e);
    if (!hit || hit.it.kind !== 'data') return;
    e.preventDefault();
    rowContextMenu(hit.it, e, isTouch() ? 'touch' : 'mouse', e.clientX, e.clientY);
  };

  /* ---------------- row drag and drop (rowDragging) ---------------- */

  /** Whether the row being dragged may be dropped here. */
  const dropAllowed = () => {
    const cfg = live.current.rowDragging;
    if (!rowDrag || !cfg) return false;
    if (rowDrag.gridId === autoId) return !!cfg.allowReordering;
    return rowDrag.group != null && rowDrag.group === cfg.group;
  };

  /** Where a drop at this event lands, as an index among the data rows (count = the end). */
  const dropGapAt = (e: DragEvent<HTMLDivElement>): number | null => {
    const t = e.target as HTMLElement;
    if (!t.closest || t.closest('.app-grid-scroll') !== e.currentTarget) return null; // a nested grid's own
    if (t.closest('.app-grid-head')) return 0;
    const count = leafRows.length;
    const rowEl = t.closest<HTMLElement>('.app-grid-body [role="row"][data-index]');
    const idx = rowEl ? Number(rowEl.dataset.index) : -1;
    const it = idx >= 0 ? viewItems[idx] : undefined;
    if (!rowEl || !it) return count;
    if (it.kind === 'data') {
      const r = rowEl.getBoundingClientRect();
      return e.clientY < r.top + r.height / 2 ? it.dataIndex : it.dataIndex + 1;
    }
    if (it.kind === 'detail' || it.kind === 'preview') {
      const owner = viewItems.find((x) => x.kind === 'data' && x.key === it.key);
      return owner?.kind === 'data' ? owner.dataIndex + 1 : count;
    }
    // A group row or group footer: before the next data row.
    const next = viewItems.slice(idx + 1).find((x) => x.kind === 'data');
    return next?.kind === 'data' ? next.dataIndex : count;
  };

  const onRowDragStart = (e: DragEvent<HTMLDivElement>) => {
    const cfg = live.current.rowDragging;
    if (!cfg) return;
    const t = e.target as HTMLElement;
    if (!t.closest || t.closest('.app-grid-scroll') !== e.currentTarget) return;
    const rowEl = t.closest<HTMLElement>('.app-grid-body [role="row"][data-index]');
    const it = rowEl ? viewItems[Number(rowEl.dataset.index)] : undefined;
    if (!it || it.kind !== 'data') return;
    rowDrag = {
      gridId: autoId,
      group: cfg.group,
      data: cfg.data,
      item: it.data,
      key: it.key,
      fromIndex: it.dataIndex,
      onRemove: (ev) => live.current.rowDragging?.onRemove?.(ev as GridRowDragEvent<T>),
    };
    try {
      e.dataTransfer?.setData('text/plain', String(it.key));
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    } catch { /* a browser that refuses drag data still drags */ }
  };

  const onRowDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!dropAllowed()) return;
    const gap = dropGapAt(e);
    if (gap == null) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    setDropGap((g) => (g === gap ? g : gap));
  };

  const onRowDragLeave = (e: DragEvent<HTMLDivElement>) => {
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    setDropGap(null);
  };

  const onRowDrop = (e: DragEvent<HTMLDivElement>) => {
    setDropGap(null);
    const src = rowDrag;
    const cfg = live.current.rowDragging;
    if (!src || !cfg || !dropAllowed()) return;
    const gap = dropGapAt(e);
    if (gap == null) return;
    e.preventDefault();
    rowDrag = null;
    if (src.gridId === autoId) {
      // DevExtreme's toIndex: where the row sits once moved.
      const to = gap > src.fromIndex ? gap - 1 : gap;
      if (to !== src.fromIndex) {
        cfg.onReorder?.({ fromIndex: src.fromIndex, toIndex: to, itemData: src.item as T, itemKey: src.key, fromData: src.data, toData: cfg.data });
      }
      return;
    }
    const ev: GridRowDragEvent<T> = { fromIndex: src.fromIndex, toIndex: gap, itemData: src.item as T, itemKey: src.key, fromData: src.data, toData: cfg.data };
    cfg.onAdd?.(ev);
    src.onRemove(ev as GridRowDragEvent<unknown>);
  };

  const onRowDragEnd = () => {
    if (rowDrag?.gridId === autoId) rowDrag = null;
    setDropGap(null);
  };

  /* ---------------- imperative API ---------------- */

  const doExport = (fileName?: string, opts?: { sheetName?: string; autoFilter?: boolean }) => exportGridToExcel({
    sheetName: opts?.sheetName,
    autoFilter: opts?.autoFilter,
    // Columns columnHiding left out are still the user's columns: they are exported.
    columns: layout.dataOrder.map((id) => colById.get(id)!),
    allColumns: [...colById.values()],
    nodes: exportNodes,
    summary,
    allRows: leafRows,
  }, fileName ?? exportOpt?.fileName ?? 'Export');

  // The same rows as the Excel export, drawn as the grid's text into the
  // screen's jsPDF document; columns keep their on-screen proportions.
  const doExportPdf: AppGridHandle<T>['exportToPdf'] = (doc, options) => {
    const shownW = new Map(layout.cols.map((c) => [c.id, c.width]));
    return drawGridPdf(doc, {
      columns: layout.dataOrder.map((id) => colById.get(id)!),
      allColumns: [...colById.values()],
      nodes: exportNodes,
      summary,
      allRows: leafRows,
    }, { widths: layout.dataOrder.map((id) => shownW.get(id) ?? layout.hiddenW[id] ?? 0), top: options?.top });
  };

  useImperativeHandle(props.ref, () => ({
    searchByText: (text: string) => setSearchInput(text),
    clearFilter: () => {
      setFilters({});
      setSearchInput('');
    },
    exportToExcel: (fileName?: string, opts?: { sheetName?: string; autoFilter?: boolean }) => doExport(fileName, opts),
    exportToPdf: doExportPdf,
    getColumnState: () => columnState,
    // The data rows on screen: rows inside a collapsed group are left out
    // (DevExtreme's getVisibleRows), in display order.
    getVisibleRows: () => items.flatMap((it) => (it.kind === 'data' ? [it.data] : [])),
    focusRow: (key: GridKey, columnId?: string) => {
      if (columnId != null && cellNavigation) setFocusCol(columnId);
      const it = viewItems.find((x) => x.kind === 'data' && String(x.key) === String(key));
      if (it) {
        pendingFocusRow.current = null;
        setFocus(it);
      } else pendingFocusRow.current = String(key);
    },
    editCell: (key: GridKey, columnId: string) => {
      const it = viewItems.find((x) => x.kind === 'data' && String(x.key) === String(key));
      if (it) {
        pendingEdit.current = null;
        openEditAt(it, columnId);
      } else pendingEdit.current = { key: String(key), colId: columnId };
    },
    expandRow: (key: GridKey) => setDetail(String(key), true),
    collapseRow: (key: GridKey) => setDetail(String(key), false),
    getScrollElement: () => scrollRef.current,
    showColumnChooser: (anchor?: HTMLElement | null) => {
      setChooserAnchor(anchor ?? rootRef.current?.querySelector<HTMLElement>('.app-grid-tool[aria-label="Columns"]') ?? rootRef.current);
    },
  }));

  // Fired when the rows shown actually change (not on every re-render, which a new
  // inline columns array would otherwise cause through the rebuilt text cache).
  const lastReady = useRef<T[] | null>(null);
  useEffect(() => {
    const prev = lastReady.current;
    if (prev && prev.length === leafRows.length && prev.every((r, i) => r === leafRows[i])) return;
    lastReady.current = leafRows;
    live.current.onContentReady?.({ visibleRowCount: leafRows.length, rows: leafRows });
  }, [leafRows]);

  // onFocusedCellChanged: after the render that moved the focused cell, so an edit committed
  // on the way out of the old cell has been reported first.
  const focusedItem = focusIndex >= 0 ? viewItems[focusIndex] : undefined;
  const focusedCellKey = cellNavigation && focusedItem?.kind === 'data' && focusCol ? `${focusedItem.id}\u0000${focusCol}` : '';
  const lastFocusedCell = useRef('');
  useEffect(() => {
    if (focusedCellKey === lastFocusedCell.current) return;
    lastFocusedCell.current = focusedCellKey;
    if (!focusedCellKey || focusedItem?.kind !== 'data' || !focusCol) return;
    const column = colById.get(focusCol);
    if (!column) return;
    live.current.onFocusedCellChanged?.({ key: focusedItem.key, data: focusedItem.data, rowIndex: focusedItem.dataIndex, column, columnId: focusCol });
  }, [focusedCellKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- state callbacks (onSortChange, onColumnStateChange) ---------------- */

  const columnState: GridColumnState = {
    order,
    hidden: order.filter((id) => hidden[id]),
    widths,
    pinned,
    sorting: sorting.map((s) => ({ id: s.id, desc: !!s.desc })),
    grouping,
  };
  const plainSortKey = JSON.stringify(sorting.filter((s) => !grouping.includes(s.id)).map((s) => ({ id: s.id, desc: !!s.desc })));
  const columnStateKey = JSON.stringify(columnState);
  const reported = useRef<{ sort: string; state: string } | null>(null);
  useEffect(() => {
    // The first render reports nothing: only changes after mount.
    if (!reported.current) {
      reported.current = { sort: plainSortKey, state: columnStateKey };
      return;
    }
    if (reported.current.sort !== plainSortKey) {
      reported.current.sort = plainSortKey;
      live.current.onSortChange?.(JSON.parse(plainSortKey) as GridSortItem[]);
    }
    if (reported.current.state !== columnStateKey) {
      reported.current.state = columnStateKey;
      live.current.onColumnStateChange?.(JSON.parse(columnStateKey) as GridColumnState);
    }
  }, [plainSortKey, columnStateKey]);

  /* ---------------- totals (memoised: 10,000 rows per item) ---------------- */

  const totalItems = summary?.totalItems;
  const totalsKey = (totalItems ?? []).map((t) => `${t.column}|${t.summaryType}|${t.showInColumn ?? ''}`).join(',');
  const totals = useMemo(() => {
    const out: Record<string, string> = {};
    for (const it of live.current.summary?.totalItems ?? []) {
      const c = getCol(it.column);
      const text = summaryText(it, computeSummary(it.summaryType, leafRows, c), c);
      const at = it.showInColumn ?? it.column;
      if (text) out[at] = out[at] ? `${out[at]}\n${text}` : text;
    }
    return out;
    // Items are read through live.current; totalsKey is what changes them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafRows, totalsKey, getCol]);

  /* ---------------- render ---------------- */

  // A band row above the column headers (a visible column with a `band`) makes the header two rows.
  const hasBands = layout.cols.some((lc) => lc.kind === 'data' && !!colById.get(lc.id)?.band);
  const headerRows = hasBands ? 2 : 1;

  const rowCtx: RowCtx<T> = {
    layout,
    width: rowWidth,
    rowH: metrics.rowH,
    getCol,
    textCache,
    search,
    searchable,
    hl,
    focusId: focusIndex >= 0 ? viewItems[focusIndex].id : null,
    focusCol,
    groupCollapsible,
    focusedRowEnabled: !!focusedRowEnabled,
    rowSelection,
    selMode,
    showChecks,
    canSelect: canSelectRow,
    editingCell,
    draft,
    editSeeded,
    draggable: !!props.rowDragging,
    dropGap,
    dataCount: leafRows.length,
    editing,
    masterDetail,
    rowPreview: props.rowPreview,
    hiddenLine: columnHiding?.showHiddenInRow ? layout.autoHidden : NONE,
    detailIsOpen,
    rowAlternationEnabled: !!rowAlternationEnabled,
    wordWrap: !!wordWrapEnabled,
    rowClassName: props.rowClassName,
    cellClassName: props.cellClassName,
    groupItems: groupItemsAll,
    footerItems,
    idPrefix: autoId,
    rowIndexBase: headerRows + 1 + (paged ? pageIndex * pageSize : 0),
    actions,
  };

  // Header controls are mouse targets only (tabIndex -1): the keyboard reaches
  // them through the grid's single tab stop (see onHeaderKeyDown).
  const headerCells = layout.cols.map((lc, ci) => {
    const hid = `${autoId}-h-${ci}`;
    const kbd = headerFocus === ci ? ' is-kbd' : '';
    if (lc.kind === 'select') {
      return (
        <div
          key={lc.id}
          id={hid}
          role="columnheader"
          aria-colindex={ci + 1}
          aria-label="Select all"
          className={`app-grid-hcell app-grid-sys app-grid-al-center${pinClass(lc)}${kbd}`}
          style={cellStyle(lc)}
          // Touch: the whole cell is the box's target (a finger misses a 16px box).
          onClick={(e) => { if (isTouch() && !(e.target as HTMLElement).closest('input')) toggleAll(); }}
        >
          <input
            type="checkbox"
            className="app-grid-check"
            aria-label="Select all"
            title="Select all"
            tabIndex={-1}
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = selectedCount > 0 && !allSelected;
            }}
            onChange={toggleAll}
          />
          {selection?.showSelectedCount && (
            <span className="app-grid-select-count" aria-live="polite" title={`${totalSelected} selected`}>
              {totalSelected > 0 ? totalSelected : ''}
            </span>
          )}
        </div>
      );
    }
    if (lc.kind === 'detail') return <div key={lc.id} id={hid} role="columnheader" aria-colindex={ci + 1} aria-label="Details" className={`app-grid-hcell app-grid-sys${pinClass(lc)}`} style={cellStyle(lc)} />;
    if (lc.kind !== 'data') return <div key={lc.id} id={hid} role="presentation" className={`app-grid-hcell app-grid-sys${pinClass(lc)}`} style={cellStyle(lc)} />;
    const c = colById.get(lc.id)!;
    const caption = columnCaption(c);
    const sortable = canSort(c);
    const sortState = effectiveSorting.find((s) => s.id === lc.id);
    const plainSorts = sorting.filter((s) => !grouping.includes(s.id));
    const sortIdx = plainSorts.findIndex((s) => s.id === lc.id);
    const filterable = canFilter(lc.id, c);
    const filtered = !!filters[lc.id];
    const resizable = allowColumnResizing && c.allowResizing !== false;
    const draggable = !!allowColumnReordering && c.allowReordering !== false;
    const mark = dropMark?.id === lc.id ? ` app-grid-drop-${dropMark.side}` : '';
    // headerCellRender: the caption stays the accessible name (visually hidden), the custom content is decoration.
    const capContent = c.headerCellRender ? (
      <>
        <span className="app-grid-sr">{caption}</span>
        <span className="app-grid-hcap-custom" aria-hidden="true">
          {c.headerCellRender({ column: c, caption, sortOrder: sortState ? (sortState.desc ? 'desc' : 'asc') : undefined })}
        </span>
      </>
    ) : <span className="app-grid-hcap-text">{caption}</span>;
    const tip = c.headerTooltip || undefined;
    return (
      <div
        key={lc.id}
        id={hid}
        role="columnheader"
        aria-colindex={ci + 1}
        aria-describedby={tip ? `${autoId}-hd-${ci}` : undefined}
        data-tip={tip}
        aria-sort={sortState ? (sortState.desc ? 'descending' : 'ascending') : sortable ? 'none' : undefined}
        className={`app-grid-hcell${pinClass(lc)}${alignClass(c)}${mark}${filtered ? ' is-filtered' : ''}${kbd}`}
        style={cellStyle(lc)}
        // Reached through the grid's aria-activedescendant, never by Tab.
        tabIndex={-1}
        draggable={draggable}
        onDragStart={draggable ? onHeaderDragStart(lc.id) : undefined}
        onDragOver={onHeaderDragOver(lc.id)}
        onDragLeave={() => setDropMark((m) => (m?.id === lc.id ? null : m))}
        onDrop={onHeaderDrop(lc.id)}
        onDragEnd={() => { dragId.current = null; setDropMark(null); setPanelOver(false); }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (pressContextMenu(e)) return;
          setMenu({ id: lc.id, x: e.clientX, y: e.clientY });
        }}
      >
        {sortable ? (
          <button
            type="button"
            className="app-grid-hcap"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              setHeaderFocus(ci);
              headerSortClick(lc.id, e);
              scrollRef.current?.focus({ preventScroll: true });
            }}
          >
            {capContent}
            {sortState && (
              <span className="app-grid-sort" aria-hidden="true">
                <i className={`bi ${sortState.desc ? 'bi-arrow-down' : 'bi-arrow-up'}`} />
                {showSortIndexes && plainSorts.length > 1 && sortIdx >= 0 && <span className="app-grid-sort-n">{sortIdx + 1}</span>}
              </span>
            )}
          </button>
        ) : (
          <span className="app-grid-hcap app-grid-hcap--static">
            {capContent}
          </span>
        )}
        {filterable && (
          <button
            type="button"
            className={`app-grid-hfilter${filtered ? ' is-active' : ''}`}
            aria-label={`Filter ${caption}`}
            title={`Filter ${caption}`}
            aria-haspopup="dialog"
            aria-expanded={filterFor?.id === lc.id}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              setHeaderFocus(ci);
              scrollRef.current?.focus({ preventScroll: true });
              setFilterFor({ id: lc.id, anchor: e.currentTarget });
            }}
          >
            <i className={`bi ${filtered ? 'bi-funnel-fill' : 'bi-funnel'}`} aria-hidden="true" />
          </button>
        )}
        {/* Pointer-only drag handle; the keyboard resizes with Ctrl+Left / Right on the header. */}
        {resizable && <div aria-hidden="true" className="app-grid-resizer" onPointerDown={startResize(lc.id, lc.width)} />}
      </div>
    );
  });

  // Band headers: one cell per run of consecutive columns with the same band (and pin).
  const bandList = props.bands ?? [];
  const bandCells: ReactNode[] = [];
  if (hasBands) {
    const bandById = new Map(bandList.map((b) => [b.id, b]));
    const cols = layout.cols;
    const bandOf = (lc: LayoutCol) => (lc.kind === 'data' ? colById.get(lc.id)?.band : undefined);
    for (let i = 0; i < cols.length;) {
      const first = cols[i];
      const id = bandOf(first);
      let j = i + 1;
      while (id !== undefined && j < cols.length && cols[j].pin === first.pin && bandOf(cols[j]) === id) j++;
      const run = cols.slice(i, j);
      const cell: LayoutCol = {
        id: `band-${i}`,
        kind: first.kind,
        width: run.reduce((s, c) => s + c.width, 0),
        pin: first.pin,
        offset: first.pin === 'right' ? run[run.length - 1].offset : first.offset,
        edge: run.some((c) => c.edge),
      };
      // A band not listed in `bands` is captioned with its id.
      const band = id !== undefined ? (bandById.get(id) ?? { id, caption: id }) : undefined;
      bandCells.push(band ? (
        <div
          key={cell.id}
          role="columnheader"
          aria-colindex={i + 1}
          aria-colspan={run.length}
          data-tip={band.headerTooltip || undefined}
          className={`app-grid-hcell app-grid-band${pinClass(cell)}${band.cssClass ? ` ${band.cssClass}` : ''}`}
          style={cellStyle(cell)}
        >
          {band.headerCellRender ? (
            <>
              <span className="app-grid-sr">{band.caption}</span>
              <span className="app-grid-hcap app-grid-hcap--static" aria-hidden="true">{band.headerCellRender()}</span>
            </>
          ) : (
            <span className="app-grid-hcap app-grid-hcap--static"><span className="app-grid-hcap-text">{band.caption}</span></span>
          )}
        </div>
      ) : (
        <div key={cell.id} role="presentation" className={`app-grid-hcell app-grid-band app-grid-band--none${pinClass(cell)}`} style={cellStyle(cell)} />
      ));
      i = j;
    }
  }
  const chooserCols = columns
    .map((c, i) => ({ id: ids[i], c }))
    .filter(({ c }) => c.showInColumnChooser !== false)
    .map(({ id, c }) => ({ id, caption: columnCaption(c), visible: !hidden[id], canHide: c.allowHiding !== false }));

  const showToolbar = !!(groupPanel?.visible || searchPanel?.visible || columnChooser?.enabled || exportOpt?.enabled || toolbarItems);
  const menuCol = menu ? colById.get(menu.id) : undefined;
  const rowCount = headerRows + items.length + (totalItems?.length ? 1 : 0);

  /* ---------------- tooltips: header tooltips, cut-off cell text ---------------- */

  const tipRef = useRef<TipApi | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const hintEnabled = (id: string) => (colById.get(id)?.cellHintEnabled ?? cellHintEnabled) !== false;

  const onTipOver = (e: MouseEvent) => {
    const tip = tipRef.current;
    const t = e.target as HTMLElement;
    if (!tip || tip.contains(t) || t.closest('.app-grid') !== rootRef.current) return; // nested grids show their own
    const head = t.closest<HTMLElement>('.app-grid-head [data-tip]');
    if (head) {
      if (tip.anchor() !== head) tip.show(head.dataset.tip!, head);
      return;
    }
    // A caption cut off with an ellipsis (a narrow or dragged-narrow column): show it in full.
    const capText = t.closest('.app-grid-hfilter') ? null : t.closest<HTMLElement>('.app-grid-hcell')?.querySelector<HTMLElement>('.app-grid-hcap-text');
    if (capText && t.closest('.app-grid-head')) {
      const cut = capText.scrollWidth > capText.clientWidth + 1 || capText.scrollHeight > capText.clientHeight + 1;
      const text = capText.textContent?.trim();
      if (cut && text) {
        if (tip.anchor() !== capText) tip.show(text, capText);
        return;
      }
    }
    const cc = t.closest<HTMLElement>('.app-grid-cc');
    const cell = cc?.closest<HTMLElement>('[data-col]');
    const titled = t.closest('[title]');
    if (cc && cell && !(titled && cell.contains(titled)) && hintEnabled(cell.dataset.col!) && isTruncated(cc, t)) {
      const text = cc.textContent?.trim();
      if (text && tip.anchor() !== cc) tip.show(text, cc);
      if (text) return;
    }
    if (tip.anchor()) tip.hide();
  };
  const onTipOut = (e: MouseEvent) => {
    const tip = tipRef.current;
    const a = tip?.anchor();
    const to = e.relatedTarget;
    if (!tip || !a) return;
    if (to instanceof Node && (a.contains(to) || tip.contains(to))) return;
    tip.hide();
  };
  // Native listeners (pointer-only hints): the grid root does not become an interactive element.
  const tipHandlers = useRef({ over: onTipOver, out: onTipOut });
  useLayoutEffect(() => {
    tipHandlers.current = { over: onTipOver, out: onTipOut };
  });
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const over = (e: MouseEvent) => tipHandlers.current.over(e);
    const out = (e: MouseEvent) => tipHandlers.current.out(e);
    el.addEventListener('mouseover', over);
    el.addEventListener('mouseout', out);
    return () => {
      el.removeEventListener('mouseover', over);
      el.removeEventListener('mouseout', out);
    };
  }, []);

  // The keyboard reaching a header with a tooltip shows it (and leaving hides it).
  useEffect(() => {
    const tip = tipRef.current;
    if (!tip) return;
    const el = headerFocus != null ? document.getElementById(`${autoId}-h-${headerFocus}`) : null;
    if (el?.dataset.tip && document.activeElement === scrollRef.current) tip.show(el.dataset.tip, el, 0);
    else if (tip.anchor()?.closest('.app-grid-head')) tip.hide();
  }, [headerFocus, autoId]);

  const headerTips = layout.cols
    .map((lc, ci) => ({ ci, tip: lc.kind === 'data' ? colById.get(lc.id)?.headerTooltip : undefined }))
    .filter((x) => x.tip);

  return (
    <div
      ref={rootRef}
      className={`app-grid${showRowLines ? ' app-grid--row-lines' : ''}${checkMode === 'onClick' && showChecks && !withChecks ? ' app-grid--checks-onclick' : ''}${showColumnLines ? ' app-grid--col-lines' : ''}${props.onRowContextMenu ? ' app-grid--row-menu' : ''}${hoverStateEnabled ? ' app-grid--hover' : ''}${wordWrapEnabled ? ' app-grid--wrap' : ''}${height === 'auto' ? ' app-grid--auto' : ''}${dropGap != null && leafRows.length === 0 ? ' app-grid--drop-empty' : ''}${className ? ` ${className}` : ''}`}
      style={{ height: height === 'auto' ? undefined : height, ['--app-grid-row-h' as string]: `${metrics.rowH}px` }}
    >
      {showToolbar && (
        <div className="app-grid-toolbar">
          {groupPanel?.visible ? (
            <div
              ref={panelRef}
              className={`app-grid-group-panel${panelOver ? ' is-over' : ''}`}
              aria-label="Grouped columns"
              role="group"
            >
              {grouping.length === 0 && <span className="app-grid-group-hint">{groupPanel.emptyPanelText ?? 'Drag a column header here to group by that column'}</span>}
              {grouping.map((id) => {
                const c = colById.get(id);
                const caption = c ? columnCaption(c) : id;
                const s = effectiveSorting.find((x) => x.id === id);
                return (
                  <span key={id} className="app-grid-chip">
                    <button type="button" className="app-grid-chip-sort" onClick={() => setSort(id, s?.desc ? 'asc' : 'desc')} aria-label={`${caption}, sorted ${s?.desc ? 'descending' : 'ascending'}`}>
                      {caption}
                      <i className={`bi ${s?.desc ? 'bi-arrow-down' : 'bi-arrow-up'}`} aria-hidden="true" />
                    </button>
                    <button type="button" className="app-grid-chip-x" onClick={() => ungroup(id)} aria-label={`Ungroup ${caption}`} title={`Ungroup ${caption}`}>
                      <i className="bi bi-x" aria-hidden="true" />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : <div className="app-grid-toolbar-spacer" />}
          {toolbarItems}
          {columnChooser?.enabled && (
            <IconButton size="small" aria-label="Columns" title="Columns" onClick={(e) => setChooserAnchor(e.currentTarget)} className="app-grid-tool">
              <i className="bi bi-layout-three-columns" aria-hidden="true" />
            </IconButton>
          )}
          {exportOpt?.enabled && (
            <IconButton size="small" aria-label="Export to Excel" title="Export to Excel" onClick={() => void doExport()} className="app-grid-tool">
              <i className="bi bi-file-earmark-excel" aria-hidden="true" />
            </IconButton>
          )}
          {searchPanel?.visible && (
            // data-enter-ignore: Enter in the search box belongs to it, never to a dialog's Enter = Save.
            <div className="app-grid-search" data-enter-ignore style={{ width: searchPanel.width ?? 220 }}>
              <i className="bi bi-search app-grid-search-icon" aria-hidden="true" />
              <TextInput value={searchInput} onValueChanged={setSearchInput} ariaLabel="Search" placeholder={searchPanel.placeholder ?? 'Search...'} mode="search" showClearButton />
            </div>
          )}
        </div>
      )}

      <div
        ref={scrollRef}
        className={`app-grid-scroll${kbdMode ? ' is-kbd-mode' : ''}`}
        // The visible width, for the empty message on a grid wider than the screen (AppGrid.css).
        style={{ '--app-grid-view-w': `${containerW}px` } as CSSProperties}
        role="grid"
        aria-label={ariaLabel}
        aria-rowcount={rowCount}
        aria-colcount={layout.cols.length}
        aria-multiselectable={selMode === 'multiple' || undefined}
        aria-activedescendant={headerFocus != null
          ? `${autoId}-h-${headerFocus}`
          : focusIndex >= 0
            ? (focusCol && viewItems[focusIndex]?.kind === 'data' ? `${autoId}-r-${focusIndex}-fc` : `${autoId}-r-${focusIndex}`)
            : undefined}
        aria-busy={loading || undefined}
        tabIndex={0}
        onKeyDown={(e) => {
          if (!kbdMode) setKbdMode(true);
          onGridKeyDown(e);
        }}
        onMouseDown={() => { if (kbdMode) setKbdMode(false); }}
        onPointerDown={onGridPointerDown}
        onPointerMove={onGridPointerMove}
        onPointerUp={cancelPress}
        onPointerCancel={cancelPress}
        onScroll={cancelPress}
        onClickCapture={(e) => {
          // The finger lifting after a long-press opened a menu: not a tap.
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={onGridClick}
        onDoubleClick={onGridDoubleClick}
        onContextMenu={onGridContextMenu}
        onDragStart={props.rowDragging ? onRowDragStart : undefined}
        onDragEnter={props.rowDragging ? onRowDragOver : undefined}
        onDragOver={props.rowDragging ? onRowDragOver : undefined}
        onDragLeave={props.rowDragging ? onRowDragLeave : undefined}
        onDrop={props.rowDragging ? onRowDrop : undefined}
        onDragEnd={props.rowDragging ? onRowDragEnd : undefined}
        onBlur={(e) => {
          // Leaving the grid hides a header tooltip the keyboard opened.
          if (e.target === e.currentTarget && tipRef.current?.anchor()?.closest('.app-grid-head')) tipRef.current.hide();
        }}
      >
        <div ref={headerRef} className="app-grid-head" role="rowgroup" style={{ width: rowWidth }}>
          {hasBands && <div role="row" aria-rowindex={1} className="app-grid-hrow app-grid-band-row">{bandCells}</div>}
          <div role="row" aria-rowindex={headerRows} className="app-grid-hrow">{headerCells}</div>
        </div>

        <GridBody<T>
          items={viewItems}
          ctx={rowCtx}
          virtual={virtual}
          scrollRef={scrollRef}
          headerH={headerH}
          footerH={footerH}
          apiRef={bodyApi}
          // Rows the grid's own search or filters hid all of: "No matches.", not "No things yet.".
          noDataText={dataSource.length > 0 && leafRows.length === 0 ? noMatchesText : noDataText}
          loading={loading}
          loadingText={loadingText}
          errorText={errorText}
          onRetry={onRetry}
        />

        {!!totalItems?.length && (
          <div ref={footerRef} className="app-grid-foot" role="rowgroup" style={{ width: rowWidth }}>
            <div role="row" aria-rowindex={rowCount} className="app-grid-frow">
              {layout.cols.map((lc) => (
                <div key={lc.id} role="gridcell" className={`app-grid-cell${pinClass(lc)}${alignClass(colById.get(lc.id))}`} style={cellStyle(lc)}>
                  {lc.kind === 'data' && totals[lc.id] ? <span className="app-grid-cc">{totals[lc.id]}</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Probes: one cell and one header caption styled like the real ones,
            measured to size rows and auto-width columns from the theme tokens. */}
        <div className="app-grid-probe" aria-hidden="true">
          <div ref={probeRef} className="app-grid-cell">Xg</div>
          <span ref={probeHeadRef} className="app-grid-hcap-text">Xg</span>
          {/* 'content' columns drawn by a cellRender: their longest rows, measured after layout. */}
          {measureCols.length > 0 && (
            <div className="app-grid-measure">
              <div ref={measureRef} className="app-grid-measure-in">
                {measureCols.map(({ id, rows }) => {
                  const c = colById.get(id);
                  if (!c?.cellRender) return null;
                  return (
                    <div key={id} data-measure={id}>
                      {rows.map((r, i) => (
                        <div key={i} className="app-grid-cell">
                          <span className="app-grid-cc">
                            {c.cellRender!({
                              data: r, key: getKey(r), value: cellValue(c, r), text: textCache.get(r, id), column: c, rowIndex: i,
                              searchText: '', highlight: (s) => s ?? '',
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {/* Header tooltips as accessible descriptions (aria-describedby). */}
        {headerTips.length > 0 && (
          <div hidden>
            {headerTips.map(({ ci, tip }) => <span key={ci} id={`${autoId}-hd-${ci}`}>{tip}</span>)}
          </div>
        )}
      </div>
      <GridTooltip apiRef={tipRef} id={`${autoId}-tip`} />

      {paged && pageCount > 1 && (
        <div className="app-grid-pager">
          <IconButton size="small" aria-label="Previous page" title="Previous page" disabled={pageIndex === 0} onClick={() => setPageIndex((p) => p - 1)}>
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </IconButton>
          <span>Page {pageIndex + 1} of {pageCount}</span>
          <IconButton size="small" aria-label="Next page" title="Next page" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex((p) => p + 1)}>
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </IconButton>
        </div>
      )}

      {filterFor && (
        <HeaderFilterPopover
          anchorEl={filterFor.anchor}
          caption={columnCaption(colById.get(filterFor.id)!)}
          values={filterValues}
          selected={filters[filterFor.id] ?? null}
          onClose={() => { setFilterFor(null); refocusGrid(); }}
          onApply={(allowed) => {
            setFilters((prev) => {
              const next = { ...prev };
              if (allowed) next[filterFor.id] = allowed;
              else delete next[filterFor.id];
              return next;
            });
            setFilterFor(null);
            refocusGrid();
          }}
        />
      )}

      {chooserAnchor && (
        <ColumnChooserPopover
          anchorEl={chooserAnchor}
          columns={chooserCols}
          onClose={() => { setChooserAnchor(null); refocusGrid(); }}
          onToggle={(id, visible) => setHidden((h) => ({ ...h, [id]: !visible }))}
        />
      )}

      <Menu
        open={!!menu}
        onClose={() => setMenu(null)}
        slotProps={{ paper: { className: 'app-grid-menu' }, transition: { onExited: refocusGrid } }}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        {menuCol && canSort(menuCol) && [
          <MenuItem key="asc" dense onClick={() => { setSort(menu!.id, 'asc'); setMenu(null); }}>Sort Ascending</MenuItem>,
          <MenuItem key="desc" dense onClick={() => { setSort(menu!.id, 'desc'); setMenu(null); }}>Sort Descending</MenuItem>,
          <MenuItem key="clear" dense onClick={() => { setSort(menu!.id, null); setMenu(null); }}>Clear Sorting</MenuItem>,
          <Divider key="d1" />,
        ]}
        {menuCol && menuCol.allowGrouping !== false && (
          <MenuItem dense onClick={() => { groupBy(menu!.id); setMenu(null); }}>Group by This Column</MenuItem>
        )}
        {grouping.length > 0 && <MenuItem dense onClick={() => { setGrouping([]); setMenu(null); }}>Ungroup All</MenuItem>}
        {menuCol && allowColumnReordering && menuCol.allowReordering !== false && [
          <Divider key="dm" />,
          <MenuItem key="ml" dense disabled={!canMove(menu!.id, -1)} onClick={() => { moveColumn(menu!.id, -1); setMenu(null); }}>Move Left</MenuItem>,
          <MenuItem key="mr" dense disabled={!canMove(menu!.id, 1)} onClick={() => { moveColumn(menu!.id, 1); setMenu(null); }}>Move Right</MenuItem>,
        ]}
        <Divider />
        <MenuItem dense onClick={() => { setPinned((p) => ({ ...p, [menu!.id]: 'left' })); setMenu(null); }}>Pin to Left</MenuItem>
        <MenuItem dense onClick={() => { setPinned((p) => ({ ...p, [menu!.id]: 'right' })); setMenu(null); }}>Pin to Right</MenuItem>
        {menu && pinned[menu.id] && (
          <MenuItem dense onClick={() => { setPinned((p) => { const n = { ...p }; delete n[menu.id]; return n; }); setMenu(null); }}>Unpin</MenuItem>
        )}
        {menuCol && menuCol.allowHiding !== false && [
          <Divider key="d2" />,
          <MenuItem key="hide" dense onClick={() => { setHidden((h) => ({ ...h, [menu!.id]: true })); setMenu(null); }}>Hide Column</MenuItem>,
        ]}
        {menu && filters[menu.id] && (
          <MenuItem dense onClick={() => { setFilters((f) => { const n = { ...f }; delete n[menu.id]; return n; }); setMenu(null); }}>Clear Filter</MenuItem>
        )}
      </Menu>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared cell helpers                                                */
/* ------------------------------------------------------------------ */

function sameJson(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

function cellStyle(lc: LayoutCol): CSSProperties {
  const s: CSSProperties = { width: lc.width };
  if (lc.pin === 'left') s.left = lc.offset;
  if (lc.pin === 'right') s.right = lc.offset;
  return s;
}

function pinClass(lc: LayoutCol): string {
  return lc.pin ? ` app-grid-pin app-grid-pin--${lc.pin}${lc.edge ? ' app-grid-pin-edge' : ''}` : '';
}

function alignClass<T>(c: AppGridColumn<T> | undefined): string {
  if (!c) return '';
  const a = c.alignment ?? (c.dataType === 'number' ? 'right' : 'left');
  const num = c.dataType === 'number' || c.dataType === 'date' || c.dataType === 'datetime' ? ' app-grid-num' : '';
  return ` app-grid-al-${a}${num}`;
}

/* ------------------------------------------------------------------ */
/*  Body: owns the virtualiser, so scrolling re-renders only this      */
/* ------------------------------------------------------------------ */

interface GridBodyProps<T> {
  items: Item<T>[];
  ctx: RowCtx<T>;
  virtual: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  headerH: number;
  footerH: number;
  apiRef: RefObject<{ scrollToIndex: (i: number) => void }>;
  noDataText: string;
  loading: boolean;
  loadingText: string;
  errorText?: string;
  onRetry?: () => void;
}

function GridBody<T>({ items, ctx, virtual, scrollRef, headerH, footerH, apiRef, noDataText, loading, loadingText, errorText, onRetry }: GridBodyProps<T>) {
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (items[i]?.kind === 'detail' ? 160 : items[i]?.kind === 'preview' ? Math.round(ctx.rowH * 0.75) : ctx.rowH),
    getItemKey: (i) => items[i]?.id ?? i,
    overscan: 10,
    scrollMargin: headerH,
    scrollPaddingStart: headerH,
    scrollPaddingEnd: footerH,
    enabled: virtual,
  });
  apiRef.current = { scrollToIndex: (i) => virtualizer.scrollToIndex(i, { align: 'auto' }) };
  // Row height changes with the theme size: re-measure everything.
  useEffect(() => { virtualizer.measure(); }, [ctx.rowH, virtualizer]);

  const measureRef = virtual ? virtualizer.measureElement : undefined;
  return (
    <div className="app-grid-body" role="rowgroup" style={{ height: virtual ? virtualizer.getTotalSize() : undefined, width: ctx.width }}>
      {loading && items.length > 0 && <div className="app-grid-progress" aria-hidden="true" />}
      {errorText ? (
        <div className="app-grid-empty app-grid-error" role="alert">
          <span>{errorText}</span>
          {onRetry && <Button size="small" variant="outlined" onClick={onRetry}>Retry</Button>}
        </div>
      ) : items.length === 0 && (
        <div className="app-grid-empty" role="status">{loading ? loadingText : noDataText}</div>
      )}
      {!errorText && virtual
        ? virtualizer.getVirtualItems().map((vi) => (
          <ItemRow<T> key={vi.key as string} it={items[vi.index]} index={vi.index} top={vi.start - headerH} ctx={ctx} measureRef={measureRef} />
        ))
        : !errorText && items.map((it, i) => <ItemRow<T> key={it.id} it={it} index={i} top={null} ctx={ctx} measureRef={undefined} />)}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Rows                                                               */
/* ------------------------------------------------------------------ */

interface ItemRowProps<T> {
  it: Item<T>;
  index: number;
  /** Virtual offset, or null in standard scrolling. */
  top: number | null;
  ctx: RowCtx<T>;
  measureRef: ((el: Element | null) => void) | undefined;
}

function ItemRowImpl<T>({ it, index, top, ctx, measureRef }: ItemRowProps<T>) {
  const { layout, rowH } = ctx;
  const pos: CSSProperties = top == null ? {} : { position: 'absolute', top: 0, left: 0, transform: `translateY(${top}px)` };
  const ariaRowIndex = ctx.rowIndexBase + index;

  if (it.kind === 'detail') {
    return (
      <div role="row" aria-label="Details" aria-rowindex={ariaRowIndex} className="app-grid-row app-grid-detail" data-index={index} ref={measureRef} style={{ ...pos, width: ctx.width }}>
        <div role="gridcell" aria-colspan={layout.cols.length} className="app-grid-detail-cell" style={{ width: ctx.width }}>
          <div className="app-grid-detail-inner">{ctx.masterDetail?.render({ data: it.data, key: it.key })}</div>
        </div>
      </div>
    );
  }

  if (it.kind === 'preview') {
    const alt = ctx.rowAlternationEnabled && it.dataIndex % 2 === 1;
    return (
      <div role="row" aria-rowindex={ariaRowIndex} className={`app-grid-row app-grid-preview${alt ? ' is-alt' : ''}`} data-index={index} ref={measureRef} style={{ ...pos, width: ctx.width }}>
        <div role="gridcell" aria-colspan={layout.cols.length} className="app-grid-preview-cell" style={{ width: ctx.width }}>
          <div className="app-grid-preview-inner">
            {ctx.rowPreview?.({ data: it.data, key: it.key, rowIndex: it.dataIndex })}
            {ctx.hiddenLine.length > 0 && <HiddenColumnsLine it={it} ctx={ctx} />}
          </div>
        </div>
      </div>
    );
  }

  if (it.kind === 'groupFooter') {
    return (
      <div role="row" aria-rowindex={ariaRowIndex} className="app-grid-row app-grid-group-footer" data-index={index} style={{ ...pos, height: rowH, width: ctx.width }}>
        {layout.cols.map((lc) => (
          <div key={lc.id} role={lc.kind === 'data' ? 'gridcell' : 'presentation'} className={`app-grid-cell${pinClass(lc)}${alignClass(ctx.getCol(lc.id))}`} style={cellStyle(lc)}>
            {lc.kind === 'data' ? <span className="app-grid-cc">{summaryCellText(ctx, ctx.footerItems, it.leaves, lc.id)}</span> : null}
          </div>
        ))}
      </div>
    );
  }

  const isFocused = ctx.focusId === it.id;

  if (it.kind === 'group') {
    const inlineItems = ctx.groupItems.filter((g) => !g.alignByColumn && !g.showInGroupFooter);
    const alignedItems = ctx.groupItems.filter((g) => g.alignByColumn && !g.showInGroupFooter);
    const inline = inlineItems
      .map((g) => summaryText(g, computeSummary(g.summaryType, it.leaves, ctx.getCol(g.column)), ctx.getCol(g.column)))
      .filter(Boolean);
    const countText = ctx.groupItems.length ? '' : `${it.leaves.length.toLocaleString('en-US')} ${it.leaves.length === 1 ? 'item' : 'items'}`;
    const suffix = [countText, ...inline].filter(Boolean).join(', ');
    // The caption spans every column up to the first aligned summary.
    const firstAligned = alignedItems.length
      ? layout.cols.findIndex((lc) => lc.kind === 'data' && alignedItems.some((g) => (g.showInColumn ?? g.column) === lc.id))
      : -1;
    const spanEnd = firstAligned > 0 ? firstAligned : layout.cols.length;
    const spanW = layout.cols.slice(0, spanEnd).reduce((s, c) => s + c.width, 0);
    const terms = [ctx.search, ctx.hl].filter(Boolean);
    const gc = ctx.getCol(it.colId);
    const custom = gc?.groupCellRender?.({
      column: gc, caption: columnCaption(gc), value: it.value, text: it.text, rows: it.leaves, count: it.leaves.length,
      summaryText: suffix, defaultText: it.caption, expanded: it.expanded, highlight: (s) => highlightText(s ?? '', terms),
    });
    return (
      <div
        id={`${ctx.idPrefix}-r-${index}`}
        role="row"
        aria-rowindex={ariaRowIndex}
        aria-expanded={ctx.groupCollapsible ? it.expanded : undefined}
        className={`app-grid-row app-grid-group${isFocused ? ' is-focused is-kbd' : ''}`}
        data-index={index}
        style={{ ...pos, height: rowH, width: ctx.width }}
      >
        <div role="gridcell" aria-colspan={spanEnd} className="app-grid-cell app-grid-group-caption" style={{ width: spanW, paddingLeft: it.depth * INDENT + 4 }}>
          <span className="app-grid-group-sticky">
            {ctx.groupCollapsible && <i className={`bi ${it.expanded ? 'bi-chevron-down' : 'bi-chevron-right'} app-grid-chevron`} aria-hidden="true" />}
            {gc?.groupCellRender ? <span className="app-grid-group-text">{custom}</span> : (
              <>
                <span className="app-grid-group-text">{highlightText(it.caption, terms)}</span>
                {suffix && <span className="app-grid-group-sum"> ({suffix})</span>}
              </>
            )}
          </span>
        </div>
        {firstAligned > 0 && layout.cols.slice(spanEnd).map((lc) => (
          <div key={lc.id} role="gridcell" className={`app-grid-cell app-grid-group-cell${pinClass(lc)}${alignClass(ctx.getCol(lc.id))}`} style={cellStyle(lc)}>
            {lc.kind === 'data' ? <span className="app-grid-cc">{summaryCellText(ctx, alignedItems, it.leaves, lc.id)}</span> : null}
          </div>
        ))}
      </div>
    );
  }

  const isSelected = !!ctx.rowSelection[it.id];
  const alt = ctx.rowAlternationEnabled && it.dataIndex % 2 === 1;
  const extra = ctx.rowClassName?.(it.data, { rowIndex: it.dataIndex, isSelected, isFocused });
  const md = ctx.masterDetail;
  const detailOpen = !!md?.enabled && ctx.detailIsOpen(it.id);
  const hasDetail = !!md?.enabled && (!md.canExpand || md.canExpand(it.data));
  const ed = ctx.editing;
  // rowDragging: the drop line above this row, or below it when it is the last one.
  const drop = ctx.dropGap == null ? ''
    : ctx.dropGap === it.dataIndex ? ' is-drop-before'
      : ctx.dropGap === ctx.dataCount && it.dataIndex === ctx.dataCount - 1 ? ' is-drop-after' : '';

  return (
    <div
      id={`${ctx.idPrefix}-r-${index}`}
      role="row"
      aria-rowindex={ariaRowIndex}
      aria-selected={ctx.selMode !== 'none' ? isSelected : undefined}
      className={`app-grid-row app-grid-data${alt ? ' is-alt' : ''}${it.hasPreview ? ' has-preview' : ''}${isFocused ? ' is-kbd' : ''}${isFocused && ctx.focusedRowEnabled ? ' is-focused' : ''}${isSelected ? ' is-selected' : ''}${drop}${extra ? ` ${extra}` : ''}`}
      draggable={ctx.draggable || undefined}
      data-key={String(it.key)}
      style={{ ...pos, height: ctx.wordWrap ? undefined : rowH, minHeight: rowH, width: ctx.width }}
      data-index={index}
      ref={ctx.wordWrap ? measureRef : undefined}
    >
      {layout.cols.map((lc, ci) => {
        // The group indent is spacing, not a cell.
        if (lc.kind === 'indent') return <div key={lc.id} role="presentation" className={`app-grid-cell app-grid-sys${pinClass(lc)}`} style={cellStyle(lc)} />;
        if (lc.kind === 'select') {
          return (
            <div
              key={lc.id}
              role="gridcell"
              className={`app-grid-cell app-grid-sys app-grid-al-center${pinClass(lc)}`}
              style={cellStyle(lc)}
              // Touch: the whole cell is the box's target, as a tap on the box itself.
              onClick={(e) => {
                if (!ctx.actions.touch() || (e.target as HTMLElement).closest('input')) return;
                e.stopPropagation();
                if (!ctx.canSelect(it.data)) return;
                ctx.actions.focus(it);
                ctx.actions.select(it.id, 'toggle', true);
              }}
            >
              <input
                type="checkbox"
                className="app-grid-check"
                checked={isSelected}
                disabled={!ctx.canSelect(it.data)}
                aria-label="Select row"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  ctx.actions.focus(it);
                  ctx.actions.select(it.id, e.shiftKey ? 'range' : 'toggle', true);
                }}
                onChange={() => undefined}
              />
            </div>
          );
        }
        if (lc.kind === 'detail') {
          return (
            <div key={lc.id} role="gridcell" className={`app-grid-cell app-grid-sys app-grid-al-center${pinClass(lc)}`} style={cellStyle(lc)}>
              {hasDetail && (
                <button
                  type="button"
                  className="app-grid-expander"
                  aria-expanded={detailOpen}
                  aria-label={detailOpen ? 'Collapse details' : 'Expand details'}
                  title={detailOpen ? 'Collapse details' : 'Expand details'}
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    ctx.actions.focus(it);
                    ctx.actions.toggleDetail(it.id);
                  }}
                >
                  <i className={`bi ${detailOpen ? 'bi-chevron-down' : 'bi-chevron-right'}`} aria-hidden="true" />
                </button>
              )}
            </div>
          );
        }
        const c = ctx.getCol(lc.id);
        if (!c) return null;
        const isEditing = ctx.editingCell?.rowId === it.id && ctx.editingCell.colId === lc.id;
        const value = cellValue(c, it.data);
        const extraCell = ctx.cellClassName?.({ value, data: it.data, column: c });
        const editable = !!(ed?.allowUpdating && c.dataField && c.allowEditing !== false && !c.calculateCellValue
          && (!ed.canEditCell || ed.canEditCell(it.data, c)));
        let content: ReactNode;
        if (isEditing) {
          content = (
            <CellEditor
              column={c}
              row={it.data}
              value={ctx.draft === UNTOUCHED ? value : ctx.draft}
              height={rowH - 4}
              selectText={ed?.selectTextOnEditStart !== false}
              seeded={ctx.editSeeded}
              onChange={ctx.actions.setDraft}
              onDone={ctx.actions.finishEdit}
              onMove={ctx.actions.moveEdit}
              onEnter={ctx.actions.moveEditDown}
            />
          );
        } else {
          // Search marks only in columns the search ran on; highlightText (marked, not
          // filtered) in every column that allows search, render-only ones included.
          const searched = ctx.search && ctx.searchable.has(lc.id) ? ctx.search : '';
          const marked = ctx.hl && c.allowSearch !== false ? ctx.hl : '';
          const terms = searched && marked ? [searched, marked] : searched || marked;
          if (c.cellRender) {
            content = c.cellRender({
              data: it.data, key: it.key, value, text: ctx.textCache.get(it.data, lc.id), column: c, rowIndex: it.dataIndex, searchText: searched,
              highlight: (s) => highlightText(s ?? '', terms),
            });
          } else {
            const text = ctx.textCache.get(it.data, lc.id);
            content = terms ? highlightText(text, terms) : text;
          }
        }
        const startOn = ed?.startEditAction === 'dblClick' ? 'dbl' : 'click';
        const cellFocus = isFocused && ctx.focusCol === lc.id;
        return (
          <div
            key={lc.id}
            id={cellFocus ? `${ctx.idPrefix}-r-${index}-fc` : undefined}
            role="gridcell"
            aria-colindex={ci + 1}
            className={`app-grid-cell${pinClass(lc)}${alignClass(c)}${c.cssClass ? ` ${c.cssClass}` : ''}${extraCell ? ` ${extraCell}` : ''}${isEditing ? ' is-editing' : ''}${editable ? ' is-editable' : ''}${cellFocus ? ' is-cell-focus' : ''}`}
            style={cellStyle(lc)}
            data-col={lc.id}
            data-edit={editable && !isEditing ? startOn : undefined}
            // A click on the open editor's cell, beside the field, keeps the field focused
            // (it would otherwise blur, which commits and closes the editor).
            onMouseDown={isEditing ? (e) => { if (!(e.target as HTMLElement).closest('.app-grid-editor')) e.preventDefault(); } : undefined}
          >
            {isEditing ? content : <span className="app-grid-cc">{content}</span>}
          </div>
        );
      })}
    </div>
  );
}

const ItemRow = memo(ItemRowImpl) as typeof ItemRowImpl;

/**
 * columnHiding.showHiddenInRow: the row's left-out columns, "Caption: value · Caption: value",
 * each drawn as its cell would be (cellRender, else the shown text, with the search marks).
 * Values with no text, and render-only columns (no value to name), are skipped.
 */
function HiddenColumnsLine<T>({ it, ctx }: { it: Item<T> & { kind: 'preview' }; ctx: RowCtx<T> }) {
  const parts: ReactNode[] = [];
  for (const id of ctx.hiddenLine) {
    const c = ctx.getCol(id);
    if (!c) continue;
    // Only values: a render-only column (an icon button) has none to name.
    if (!c.dataField && !c.calculateCellValue) continue;
    const text = ctx.textCache.get(it.data, id);
    if (text === '') continue;
    const searched = ctx.search && ctx.searchable.has(id) ? ctx.search : '';
    const marked = ctx.hl && c.allowSearch !== false ? ctx.hl : '';
    const terms = searched && marked ? [searched, marked] : searched || marked;
    const content: ReactNode = c.cellRender
      ? c.cellRender({
        data: it.data, key: it.key, value: cellValue(c, it.data), text, column: c, rowIndex: it.dataIndex, searchText: searched,
        highlight: (s) => highlightText(s ?? '', terms),
      })
      : terms ? highlightText(text, terms) : text;
    if (content == null || content === '' || content === false) continue;
    if (parts.length) parts.push(<span key={`${id}-sep`} aria-hidden="true">{' · '}</span>);
    // A drawn value (a chip, an icon button with a touch-sized box) sits centred on the
    // line rather than on its baseline, which left a gap under a tall icon.
    const drawn: CSSProperties | undefined = c.cellRender ? { display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle' } : undefined;
    parts.push(<span key={id} className="app-grid-hidden-value" data-col={id} style={drawn}>{columnCaption(c)}: {content}</span>);
  }
  if (!parts.length) return null;
  // The preview line's italic is for notes; these are the row's own values.
  return <span className="app-grid-hidden-line" style={{ display: 'block', fontStyle: 'normal' }}>{parts}</span>;
}

function summaryCellText<T>(ctx: RowCtx<T>, list: GridSummaryItem[], rows: T[], colId: string): string {
  return list
    .filter((it) => (it.showInColumn ?? it.column) === colId)
    .map((it) => summaryText(it, computeSummary(it.summaryType, rows, ctx.getCol(it.column)), ctx.getCol(it.column)))
    .filter(Boolean)
    .join('\n');
}

/* ------------------------------------------------------------------ */
/*  Cell editor                                                        */
/* ------------------------------------------------------------------ */

interface CellEditorProps<T> {
  column: AppGridColumn<T>;
  /** The row being edited (editorOptions.lookupDataSource and editCellRender are per row). */
  row: T;
  value: unknown;
  height: number;
  selectText: boolean;
  /** Opened by a typed character: the caret goes after it, nothing is selected. */
  seeded?: boolean;
  onChange: (v: unknown) => void;
  onDone: (commit: boolean, refocus?: boolean, via?: 'enter') => void;
  /** Tab: commit and open the next editable cell. False when there is none,
   *  and Tab then moves on out of the grid. */
  onMove: (back: boolean) => boolean;
  /** Enter, after the field committed its text: moves the editor down a row
   *  (editing.enterKeyDirection 'column'). False: Enter only commits. */
  onEnter?: () => boolean;
}

/** One inline editor per cell, built from the shared form fields. Enter or
 *  leaving the cell commits, Escape cancels; Tab / Shift+Tab commit and open
 *  the next / previous editable cell. Enter and Escape hand the keyboard back
 *  to the grid. */
function CellEditor<T>({ column, row, value, height, selectText, seeded = false, onChange, onDone, onMove, onEnter }: CellEditorProps<T>) {
  const caption = columnCaption(column);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const input = wrapRef.current?.querySelector('input');
    input?.focus();
    if (seeded && input) {
      const end = input.value.length;
      input.setSelectionRange?.(end, end);
    } else if (selectText) input?.select();
    // Once, when the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Set once this editor has finished by key, so the `focusout` that follows
   * does not commit a second time. That second commit used to run against
   * whatever cell was open by then - on Tab, the one just opened - closing it
   * immediately.
   */
  const finishedRef = useRef(false);
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      finishedRef.current = true;
      onDone(false, true);
    } else if (e.key === 'Enter' && (e.target as HTMLElement).getAttribute('aria-expanded') !== 'true') {
      e.preventDefault();
      e.stopPropagation();
      finishedRef.current = true;
      // Let the field commit its typed text first. The key stops here, so the
      // field's own Enter handler never sees it: a number field commits on blur
      // instead (typed text past min / max then commits as the limit, rather
      // than the last value that was in range).
      const t = e.target as HTMLElement;
      if (!column.lookup && !column.editCellRender && column.dataType === 'number' && t instanceof HTMLInputElement) t.blur();
      // Data entry (enterKeyAction 'startEdit'): the editor moves down a row; otherwise
      // it closes ('moveFocus' then moves the focused cell down).
      setTimeout(() => { if (!enterRef.current?.()) doneRef.current(true, true, 'enter'); }, 0);
    } else if (e.key === 'Tab' && !e.ctrlKey && !e.altKey && !e.metaKey) {
      // Stay in the grid and step to the next editable cell. Only when there
      // is none does Tab do its usual thing and move focus out: the browser
      // moves focus first, with this editor still on the page, and the
      // focusout that follows commits it. Closing it here instead would take
      // the focused field away before the browser had moved on from it.
      if (onMove(e.shiftKey)) {
        finishedRef.current = true;
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  const eo = column.editorOptions;
  // A per-row list is worked out once, as the editor opens (onEditorPreparing).
  const [rowOptions] = useState(() => eo?.lookupDataSource?.(row));
  let field: ReactNode;
  if (column.editCellRender) {
    field = column.editCellRender({ data: row, value, setValue: onChange });
  } else if (column.lookup) {
    field = (
      <SelectField
        ariaLabel={caption}
        dataSource={rowOptions ?? (column.lookup.dataSource as readonly Record<string, unknown>[])}
        valueExpr={column.lookup.valueExpr}
        displayExpr={column.lookup.displayExpr}
        value={value}
        onValueChanged={(v) => onChange(v)}
        height={height}
        showClearButton={!!eo?.showClearButton}
        searchEnabled={eo?.searchEnabled}
        autoOpen={!!eo?.openOnFocus}
      />
    );
  } else if (column.dataType === 'number') {
    field = (
      <NumberField
        ariaLabel={caption}
        value={value as number | null}
        onValueChanged={onChange}
        height={height}
        min={eo?.min}
        max={eo?.max}
        decimals={eo?.decimals}
        showSpinButtons={eo?.showSpinButtons}
        showClearButton={eo?.showClearButton}
      />
    );
  } else {
    field = (
      <TextInput
        ariaLabel={caption}
        value={value == null ? '' : String(value)}
        onValueChanged={onChange}
        height={height}
        maxLength={eo?.maxLength}
        showClearButton={eo?.showClearButton}
      />
    );
  }
  // Native listeners: the wrapper only intercepts keys and focus leaving its own field.
  const keyRef = useRef(onKeyDown);
  const doneRef = useRef(onDone);
  const enterRef = useRef(onEnter);
  useLayoutEffect(() => {
    keyRef.current = onKeyDown;
    doneRef.current = onDone;
    enterRef.current = onEnter;
  });
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const key = (e: KeyboardEvent) => keyRef.current(e as unknown as ReactKeyboardEvent);
    // Clicks inside the editor are stopped in React (on the wrapper below), not
    // natively, so the field's own React handlers (clear, spin) still get them.
    // Those buttons never take the focus from the input: a clear button goes
    // away once the text is empty, and focus would go with it, leaving an open
    // editor that Enter and Escape can no longer reach.
    const down = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('button')) e.preventDefault();
    };
    const out = (e: FocusEvent) => {
      // A key already finished this editor; committing again here would act
      // on whichever cell is open by now.
      if (finishedRef.current) return;
      const next = e.relatedTarget as Node | null;
      // Focus moving into the select's popup list is still "in" the editor.
      if (next && (el.contains(next) || (next as HTMLElement).closest?.('.MuiAutocomplete-popper'))) return;
      finishedRef.current = true;
      doneRef.current(true);
    };
    el.addEventListener('keydown', key);
    el.addEventListener('mousedown', down);
    el.addEventListener('focusout', out);
    return () => {
      el.removeEventListener('keydown', key);
      el.removeEventListener('mousedown', down);
      el.removeEventListener('focusout', out);
    };
  }, []);
  return (
    // Clicks inside the editor stay out of the grid's row handlers. Stopped in
    // React rather than natively, so the field's own React handlers (a lookup's
    // or number field's Clear button) still get them.
    <div ref={wrapRef} className="app-grid-editor" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      {field}
    </div>
  );
}
